import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import { getKitsConProductos, getBarriosActivos, crearMiSuscripcion, crearPreapprovalCliente } from '../../lib/portalClienteApi'
import { geocodificar, validarBarrioConDireccion } from '../../lib/geo'
import MapPin from '../../components/MapPin'
import BloqueFacturacion from '../../components/BloqueFacturacion'
import TurnstileWidget from '../../components/TurnstileWidget'
import MpTarjetaBrick from '../../components/MpTarjetaBrick'
import MpSecureFieldsSusc from '../../components/MpSecureFieldsSusc'
import { ScrollHintPill } from '../../components/tienda-v5'
import { consolidarProductosKit, presentacionTotal } from '../../lib/kitDisplay'
import { VENTANAS_HORARIAS, labelVentana } from '../../lib/ventanasHorarias'
import { trackEvent } from '../../lib/metaPixel'

/** Mapeo segmento de hogar → códigos de kits que aplican */
const SEGMENTOS = [
  {
    id: 'solo',
    titulo: 'Vivo solo o en pareja',
    sub: '1 a 2 personas',
    icono: '👤',
    detalle: 'Lo justo para mantener la casa al día sin que te sobre nada.',
    kits: ['solo', 'duo'],
  },
  {
    id: 'familia',
    titulo: 'Familia tipo',
    sub: '3 a 4 personas',
    icono: '👨‍👩‍👧',
    detalle: 'Cubre cocina, baño y limpieza general. Es nuestro perfil más elegido.',
    kits: ['familiar', 'confort'],
  },
  {
    id: 'familia_grande',
    titulo: 'Familia grande',
    sub: '5 o más personas · casa con perro/gato',
    icono: '🏡',
    detalle: 'Más cantidad y más variedad para que nunca se te termine nada.',
    kits: ['hogar_plus', 'exclusive'],
  },
  {
    id: 'comercio',
    titulo: 'Comercio u oficina',
    sub: 'Almacén, bar, estudio, oficina',
    icono: '🏢',
    detalle: 'Productos de uso intensivo pensados para uso comercial.',
    kits: ['comercio_local', 'estudio_oficina'],
  },
]

const TIER_LABEL = { essential: 'Essential', premium: 'Premium', comercial: 'Comercial' }

// Split "Juan Pérez García" → { firstName: "Juan", lastName: "Pérez García" }.
// Si sólo hay un nombre, se repite en apellido (MP acepta pero no dispara antifraude negativo).
function splitNombre(nombreCompleto) {
  const partes = (nombreCompleto || '').trim().split(/\s+/).filter(Boolean)
  if (partes.length === 0) return { firstName: '', lastName: '' }
  if (partes.length === 1) return { firstName: partes[0], lastName: partes[0] }
  return { firstName: partes[0], lastName: partes.slice(1).join(' ') }
}

// MP UY acepta 'CI' y 'Otro' en identification.type. RUT (empresa) suele fallar en preapproval
// natural-person → mapeamos a 'Otro' para no romper. Pasaporte tampoco es CI → 'Otro'.
const DOC_TIPO_MP = { ci: 'CI', rut: 'Otro', pasaporte: 'Otro' }
function buildIdentificationMP(tipo, numero) {
  if (!tipo || !numero) return null
  const num = String(numero).replace(/\D/g, '')
  if (num.length < 6) return null
  return { type: DOC_TIPO_MP[tipo] || 'CI', number: num }
}

/**
 * Calcula el día D-1 a las 16hs Mvd para una fecha de entrega.
 * Si D-1 cae sábado (6) o domingo (0), snap a viernes.
 * Devuelve string formateado en es-UY.
 * Ver `06-financiero/POLITICA_COBRO.md` sección 1.
 */
function calcularDCorte(fechaEntrega) {
  if (!fechaEntrega) return null
  const entrega = new Date(fechaEntrega + 'T12:00:00')
  let corte = new Date(entrega)
  corte.setDate(corte.getDate() - 1)
  if (corte.getDay() === 0) corte.setDate(corte.getDate() - 2) // dom → vie
  if (corte.getDay() === 6) corte.setDate(corte.getDate() - 1) // sáb → vie
  return corte.toLocaleDateString('es-UY', {
    weekday: 'long', day: 'numeric', month: 'long'
  })
}

export default function NuevaSuscripcion() {
  const { user, profile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const kitParam = searchParams.get('kit') // ej: 'duo' cuando viene desde /tienda

  // Meta Pixel: InitiateCheckout al entrar al wizard (una vez por mount)
  useEffect(() => {
    trackEvent('InitiateCheckout', { content_name: 'wizard_suscripcion', content_ids: kitParam ? [kitParam] : [] })
  }, [])

  // Sub-fase 4.3 (2026-07-23): /suscribirme es PÚBLICO. Antes redirigíamos duro a /login
  // cuando !user → el visitante perdía el wizard. Ahora los pasos 0-3 (Hogar/Kit/Dirección/Día)
  // corren sin sesión (ya persisten en localStorage), y recién en el paso 4 (Pagar) pedimos crear
  // cuenta con un panel inline (<SignupInline>). Ese panel dispara el signup magic-link EXISTENTE
  // con next=/suscribirme → el user activa por email, vuelve, localStorage restaura el paso 4 ya
  // logueado y termina el pago. La ruta /cliente/nueva-suscripcion sigue bajo RequireAuth (user
  // garantizado), así que el flujo autenticado no cambia — el panel inline sólo aparece en /suscribirme.

  // ──── Persistencia del wizard en localStorage ────
  // Si el usuario sale a otra pantalla y vuelve, mantenemos su avance.
  // Se limpia cuando confirma la suscripción exitosamente.
  const STORAGE_KEY = 'limpack_wizard_susc'
  const guardado = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : null
    } catch { return null }
  })()

  // 0 segmento · 1 kit · 2 dirección · 3 día · 4 confirmar
  const [paso, setPaso] = useState(guardado?.paso ?? 0)
  const [kits, setKits] = useState([])
  const [barrios, setBarrios] = useState([])
  const [coords, setCoords] = useState(guardado?.coords ?? null)
  const [coordsExactas, setCoordsExactas] = useState(guardado?.coordsExactas ?? false)
  const [validacionBarrio, setValidacionBarrio] = useState(null)
  const [geocodando, setGeocodando] = useState(false)
  const [creando, setCreando] = useState(false)
  // Task #4 (8-jul-2026): datos para el Brick de tarjeta. Cuando != null, mostramos el Brick
  // en vez del formulario. Al tokenizar, llamamos a mp-crear-preapproval con card_token_id.
  const [datosTarjeta, setDatosTarjeta] = useState(null)
  // Suscripción 3DS (flag susc_flujo_mes1): 'preapproval' (legacy, default) | 'payment_3ds' (nuevo,
  // Secure Fields + mes1 3DS). DORMIDO hasta que la key en app_config pase a 'payment_3ds'.
  const [flujoMes1, setFlujoMes1] = useState('preapproval')
  // Task #7 (18-jul-2026 · FIX 2): session_id único por sesión del wizard. Se propaga a
  // MpTarjetaBrick (log-front-error) y a mp-crear-preapproval v21 (signup_errors_log).
  // Encadena front↔edge para diagnóstico completo. Mismo UUID aunque el user reintente.
  const sessionIdRef = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : String(Date.now()) + Math.random().toString(36).slice(2)
  )
  // Overrides locales para nombre/documento cuando el perfil viene incompleto y el mini-form
  // los completa justo antes del Brick. Persistimos vía UPDATE en profiles + local para el Brick.
  const [perfilOverride, setPerfilOverride] = useState(null)  // { nombre, documento }
  const [err, setErr] = useState(null)
  // P0 antifraude (2026-07-22 · item 3): tras un rechazo cc_rejected_high_risk bloqueamos el
  // reintento inmediato con la misma compra. Guarda el timestamp hasta el que dura el bloqueo.
  const [highRiskUntil, setHighRiskUntil] = useState(null)
  const [verTodos, setVerTodos] = useState(false)
  const [tierActivo, setTierActivo] = useState(guardado?.tierActivo ?? 'essential')
  const [kitExpandido, setKitExpandido] = useState(null)
  const [perfilFacturacionId, setPerfilFacturacionId] = useState(guardado?.perfilFacturacionId ?? null)
  const [reanudadoAvisoVisible, setReanudadoAvisoVisible] = useState(!!guardado)
  const [kitDesdeTiendaVisible, setKitDesdeTiendaVisible] = useState(false)
  const [direccionPrellenada, setDireccionPrellenada] = useState(false)

  // Fix 7-jul-2026 (Nico): merge con defaults para que localStorage viejo sin
  // calle/numero no rompa el wizard (guarda: form.calle.trim() con undefined).
  const [form, setForm] = useState(() => {
    const defaults = {
      segmento: '',
      alias: '',
      kitCodigo: '',
      barrioId: '',
      direccion: '',  // string completo, se compone de calle+numero — mantiene compat
      calle: '',
      numero: '',
      apto: '',
      tipoVivienda: '',
      diaEntrega: '',
      ventana: 'manana',
      primeraFechaISO: '',  // NUEVO 8-jul: fecha ISO de la primera entrega (reemplaza semanaMes)
      notas: '',
    }
    // Si venimos con localStorage viejo, parseamos direccion en calle+numero
    if (guardado?.form) {
      const merged = { ...defaults, ...guardado.form }
      if ((!merged.calle && !merged.numero) && merged.direccion) {
        const m = merged.direccion.trim().match(/^(.+?)\s+(\d+\s*[a-zA-Z]?)$/)
        if (m) { merged.calle = m[1].trim(); merged.numero = m[2].trim() }
        else { merged.calle = merged.direccion.trim() }
      }
      return merged
    }
    return defaults
  })

  // Guardar en localStorage cada vez que cambia algo relevante
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        paso, form, coords, coordsExactas, tierActivo, perfilFacturacionId,
        ts: Date.now(),
      }))
    } catch {}
  }, [paso, form, coords, coordsExactas, tierActivo, perfilFacturacionId])

  function limpiarWizard() {
    try { localStorage.removeItem(STORAGE_KEY) } catch {}
    setReanudadoAvisoVisible(false)
    setPaso(0)
    setForm({
      segmento: '', alias: '', kitCodigo: '', barrioId: '', direccion: '', calle: '', numero: '',
      apto: '', tipoVivienda: '', diaEntrega: '', ventana: 'manana',
      primeraFechaISO: '', notas: '',
    })
    setCoords(null); setCoordsExactas(false); setPerfilFacturacionId(null)
    setTierActivo('essential')
  }

  useEffect(() => {
    Promise.all([getKitsConProductos(), getBarriosActivos()]).then(([k, b]) => {
      setKits(k); setBarrios(b)
    })
  }, [])

  // Prerellenar dirección desde profile si el user ya la tiene guardada
  // (viene de /cliente/mi-direccion o de una suscripción previa).
  // No sobrescribe si el usuario ya empezó a completar el form manualmente.
  // Fix 8-jul-2026 (Nico): también parsear calle+número desde profile.direccion legacy
  // (bug: se mostraba aviso "usamos tu dirección guardada" pero calle/número quedaban vacíos)
  useEffect(() => {
    if (!profile) return
    if (form.direccion || form.calle || form.numero || form.barrioId) return // ya hay data, no toco
    if (!profile.direccion && !profile.barrio_id) return // profile vacío, nada que prellenar
    let calle = ''
    let numero = ''
    if (profile.direccion) {
      const m = profile.direccion.trim().match(/^(.+?)\s+(\d+\s*[a-zA-Z]?)$/)
      if (m) { calle = m[1].trim(); numero = m[2].trim() }
      else { calle = profile.direccion.trim() }
    }
    setForm(f => ({
      ...f,
      direccion: profile.direccion || f.direccion,
      calle: calle || f.calle,
      numero: numero || f.numero,
      apto: profile.apto || f.apto,
      barrioId: profile.barrio_id || f.barrioId,
    }))
    setDireccionPrellenada(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile])

  // Flag susc_flujo_mes1: define qué form de tarjeta se usa. Default 'preapproval' (legacy, Brick).
  // El flujo nuevo (payment_3ds) se activa si:
  //   (a) susc_flujo_mes1 === 'payment_3ds' global (activación total), O
  //   (b) el user está en la allowlist susc_flujo_mes1_canary_uids (canary scoped por usuario).
  // FAIL-SAFE: ante cualquier error/duda → 'preapproval' (legacy). Solo el user del canary ve el
  // flujo nuevo; el resto (Luján, signups reales) sigue 100% en legacy.
  useEffect(() => {
    let cancel = false
    supabase.from('app_config').select('key, value')
      .in('key', ['susc_flujo_mes1', 'susc_flujo_mes1_canary_uids'])
      .then(({ data }) => {
        if (cancel || !data) return
        const map = {}
        for (const row of data) { let v = row.value; try { v = JSON.parse(v) } catch {} map[row.key] = v }
        const globalFlag = String(map.susc_flujo_mes1 ?? 'preapproval').trim()
        let canaryUids = []
        try {
          const raw = map.susc_flujo_mes1_canary_uids
          canaryUids = Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw) : [])
          if (!Array.isArray(canaryUids)) canaryUids = []
        } catch { canaryUids = [] }
        const esCanary = !!user?.id && canaryUids.includes(user.id)
        setFlujoMes1(globalFlag === 'payment_3ds' || esCanary ? 'payment_3ds' : 'preapproval')
      })
    return () => { cancel = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Auto-geocodificar cuando la dirección viene prellenada del profile
  // (para que el mapa aparezca sin obligar al cliente a tocar "Verificar")
  useEffect(() => {
    if (!direccionPrellenada) return
    if (coords) return // ya se geocodificó
    if (!form.direccion || !form.barrioId) return
    if (barrios.length === 0) return
    // Barrio seleccionado tiene que existir en la lista
    if (!barrios.find(b => b.id === form.barrioId)) return
    geocodear()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direccionPrellenada, form.direccion, form.barrioId, barrios])

  // Si viene con ?kit=<codigo> (desde /tienda o compartido por WhatsApp),
  // auto-selecciona el kit, tier y segmento, y salta directo al paso 2 (DIRECCIÓN).
  // Prioridad sobre el localStorage guardado — el link "manda".
  useEffect(() => {
    if (!kitParam || kits.length === 0) return
    const kit = kits.find(k => k.codigo === kitParam)
    if (!kit) return
    // Segmento al que pertenece este kit (para preseleccionar en caso de "volver a HOGAR")
    const seg = SEGMENTOS.find(s => s.kits.includes(kit.codigo))
    setForm(f => ({
      ...f,
      kitCodigo: kit.codigo,
      segmento: seg?.id || f.segmento,
    }))
    setTierActivo(kit.tier || 'essential')
    // Saltar directo al paso 2 (DIRECCIÓN) — el cliente ya eligió kit en la tienda
    setPaso(2)
    setReanudadoAvisoVisible(false)
    setKitDesdeTiendaVisible(true)
    // Sacar el query param de la URL para que si el usuario refresca no vuelva a saltar
    const newUrl = window.location.pathname
    window.history.replaceState({}, '', newUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kits, kitParam])

  // Nuevo 8-jul-2026 (task #36): cliente elige fecha directa de primera entrega
  // dentro de las próximas 6 fechas disponibles del barrio (mié/vie para Malvín, etc).
  // Después arranca el ciclo mensual MP calendario.

  const segmentoElegido = SEGMENTOS.find(s => s.id === form.segmento)
  const kitElegido = kits.find(k => k.codigo === form.kitCodigo)
  const barrioElegido = barrios.find(b => b.id === form.barrioId)
  const diasDisponibles = barrioElegido?.dias || []

  // Si el barrio tiene un solo día de reparto, lo preseleccionamos automáticamente
  // así el usuario no tiene que tocar el select. También se aplica si cambia
  // de barrio y el día anterior ya no aplica al nuevo barrio.
  useEffect(() => {
    if (diasDisponibles.length === 0) return
    if (diasDisponibles.length === 1 && form.diaEntrega !== diasDisponibles[0]) {
      setForm(f => ({ ...f, diaEntrega: diasDisponibles[0] }))
    } else if (form.diaEntrega && !diasDisponibles.includes(form.diaEntrega)) {
      // El día elegido no está en el nuevo barrio → resetear
      setForm(f => ({ ...f, diaEntrega: '' }))
    }
  }, [form.barrioId, diasDisponibles.join(',')])

  // Calcula el momento de corte para una fecha de entrega.
  // Corte = D-1 a las 16:00 hora Mvd, con snap a viernes si D-1 cae sáb/dom.
  // Devuelve un Date (en timezone local del navegador). Asume que el cliente
  // está en Mvd o cerca — para clientes uruguayos esto es exacto.
  function calcularCorte(fechaEntrega) {
    const d = new Date(fechaEntrega)
    d.setDate(d.getDate() - 1)
    const dow = d.getDay()
    if (dow === 6) d.setDate(d.getDate() - 1)      // sáb → vie
    else if (dow === 0) d.setDate(d.getDate() - 2) // dom → vie
    d.setHours(16, 0, 0, 0)
    return d
  }

  // Helper NUEVO 8-jul-2026 (task #36): devuelve las próximas N fechas que caen en
  // los días del barrio Y que todavía no pasaron el corte D-1 16hs Mvd.
  // Reemplaza a proximaFechaDia + semana_mes del flow viejo.
  // Ejemplo Malvín (mié y vie): vie 10-jul, mié 15-jul, vie 17-jul, mié 22-jul, vie 24-jul, mié 29-jul
  function proximasFechasDelBarrio(diasBarrio, cantidad = 6) {
    if (!diasBarrio || diasBarrio.length === 0) return []
    // BUG 8-jul-2026: los días de la BD (barrios.dias_reparto) vienen sin tildes
    // ("miercoles", "sabado"), así que el array debe estar sin tildes para matchear.
    // Además normalizamos el input por si algún barrio quedó con tilde en la BD.
    const DIAS = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado']
    const norm = s => String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    const targets = new Set(diasBarrio.map(d => DIAS.indexOf(norm(d))).filter(i => i >= 0))
    const ahora = new Date()
    const hoy = new Date()
    hoy.setHours(0, 0, 0, 0)
    const fechas = []
    // Scan 90 días adelante para cubrir barrios con pocos días de reparto
    for (let offset = 1; offset <= 90 && fechas.length < cantidad; offset++) {
      const d = new Date(hoy)
      d.setDate(d.getDate() + offset)
      if (!targets.has(d.getDay())) continue
      if (calcularCorte(d) <= ahora) continue
      fechas.push(d)
    }
    return fechas
  }

  // Filtrar kits según segmento + tier (o todos si verTodos)
  const kitsVisibles = useMemo(() => {
    if (verTodos) return kits
    if (!segmentoElegido) return []
    const codigosOk = segmentoElegido.kits
    let filtrados = kits.filter(k => codigosOk.includes(k.codigo))
    // Si es comercio mostramos los 2 sin tabs; si no, filtramos por tier activo
    if (form.segmento !== 'comercio') {
      filtrados = filtrados.filter(k => k.tier === tierActivo)
    }
    return filtrados
  }, [kits, segmentoElegido, tierActivo, verTodos, form.segmento])

  async function geocodear() {
    setErr(null); setCoordsExactas(false); setValidacionBarrio(null)
    if (!barrioElegido) return
    setGeocodando(true)
    try {
      if (form.direccion.trim()) {
        // Geocoding sin forzar el barrio en la query (para detectar el real)
        const r = await geocodificar(form.direccion)
        if (r) {
          setCoords({ lat: r.lat, lng: r.lng })
          setCoordsExactas(true)
          const v = validarBarrioConDireccion(r.barrioDetectado, barrioElegido.nombre, barrios)
          setValidacionBarrio(v)
          return
        }
      }
      const rb = await geocodificar(barrioElegido.nombre)
      if (rb) setCoords({ lat: rb.lat, lng: rb.lng })
    } finally { setGeocodando(false) }
  }

  function aplicarSugerenciaBarrio() {
    if (!validacionBarrio?.sugerenciaActivo) return
    setForm(f => ({ ...f, barrioId: validacionBarrio.sugerenciaActivo.id, diaEntrega: '' }))
    setValidacionBarrio(null)
  }

  async function confirmar() {
    setErr(null); setCreando(true)
    try {
      if (!form.kitCodigo) throw new Error('Falta elegir kit')
      if (!form.barrioId) throw new Error('Falta elegir barrio')
      if (!form.direccion.trim()) throw new Error('Falta la dirección')
      if (!form.diaEntrega) throw new Error('Falta elegir día de entrega')
      if (!form.primeraFechaISO) throw new Error('Falta elegir fecha de primera entrega')
      if (!perfilFacturacionId) throw new Error('Elegí cómo te facturamos antes de continuar')

      // Calcular D-1 (con snap fin de semana) de la primera entrega — start_date del preapproval
      const primeraFecha = new Date(form.primeraFechaISO + 'T12:00:00')
      const corteDate = calcularCorte(primeraFecha)  // Date con snap sáb/dom→vie
      const startDateISO = corteDate.toISOString().slice(0, 10)  // ISO date del cobro

      // Día del mes en que arranca el cobro (MP usa esto como ancla mensual)
      const diaCobroMes = corteDate.getDate()

      // 1. Crear cliente + entrega (RPC ya existente)
      const cliente = await crearMiSuscripcion({
        alias: form.alias,
        kitCodigo: form.kitCodigo,
        perfilFacturacionId,
        barrioId: form.barrioId,
        direccion: form.direccion,
        apto: form.apto,
        tipoVivienda: form.tipoVivienda,
        diaEntrega: form.diaEntrega,
        ventana: form.ventana,
        semanaMes: '1',  // legacy field, no se usa en nuevo flow — hardcoded para compat
        diaCobroMes,
        notas: form.notas,
        lat: coords?.lat, lng: coords?.lng,
        primeraEntregaFecha: form.primeraFechaISO,
        primeraEntregaRecargo: 0,
        primeraEntregaTipo: 'esperar',
      })

      const clienteId = cliente?.id
      if (!clienteId) throw new Error('No se pudo crear la suscripción')

      // 2. Monto del mes 1.
      //  - Flujo nuevo (payment_3ds): precio PLANO = precio_susc (todos los meses igual).
      //  - Flujo legacy (preapproval): precio_unica (mes 1) + ladder del cron. INTACTO.
      const kit = kits.find(k => k.codigo === form.kitCodigo)
      const baseMonto = flujoMes1 === 'payment_3ds'
        ? (kit?.precio_susc || kit?.precio_unica)
        : (kit?.precio_unica || kit?.precio_susc)
      const monto = Number(baseMonto || 0)
      if (!monto) throw new Error('Kit sin precio configurado')

      // 3. Crear preapproval MP → devuelve init_point (checkout MP para autorizar)
      const email = profile?.email || user?.email
      if (!email) throw new Error('Falta email del cliente')

      // Task #4 (8-jul-2026): en vez de redirect a checkout hosted (que sufre del bug ESC
      // cuando el cliente tiene sesión MP con tarjetas guardadas), mostramos el Brick
      // embebido para tokenizar la tarjeta en el navegador. Al tokenizar, llamamos a
      // mp-crear-preapproval con card_token_id → status=authorized directo.
      try { localStorage.removeItem(STORAGE_KEY) } catch {}
      setDatosTarjeta({ clienteId, monto, startDate: startDateISO, email, kitCodigo: form.kitCodigo })
      setCreando(false)
      // Si el profile aún no tiene documento, el mini-form abajo lo pedirá antes del Brick.
    } catch (e) { setErr(e.message); setCreando(false) }
  }

  /**
   * Callback del MpTarjetaBrick cuando MP tokenizó la tarjeta en el navegador.
   * Manda el token al backend para crear preapproval con status=authorized directo.
   */
  async function onTarjetaTokenizada(cardTokenId, payerId, _cardData, deviceSessionId) {
    if (!datosTarjeta) return
    setCreando(true); setErr(null)
    try {
      // Payer completo para reforzar antifraude MP: nombre + apellido + doc.
      const nombreEfectivo = perfilOverride?.nombre || profile?.nombre || ''
      const docEfectivo = perfilOverride?.documento || profile?.documento || ''
      const tipoEfectivo = perfilOverride?.documento ? 'ci' : (profile?.documento_tipo || 'ci')
      const { firstName, lastName } = splitNombre(nombreEfectivo)
      const identification = buildIdentificationMP(tipoEfectivo, docEfectivo)
      const resp = await crearPreapprovalCliente({
        clienteId: datosTarjeta.clienteId,
        email: datosTarjeta.email,
        monto: datosTarjeta.monto,
        startDate: datosTarjeta.startDate,
        cardTokenId,
        payerId,
        firstName,
        lastName,
        identification,
        sessionId: sessionIdRef.current,
        deviceSessionId,  // P0 antifraude: Device ID → header X-meli-session-id en el edge
      })
      if (resp?.status === 'authorized' || resp?.autorizado_directo) {
        // Meta Pixel: dos eventos al confirmar la suscripción
        //  - Subscribe: evento nativo de suscripción (mejor para audiencias de retención)
        //  - Purchase: evento canónico de conversión con value (lo que optimizan las
        //    campañas Meta Ads por CPA/ROAS)
        const monto = Number(datosTarjeta.monto || 0)
        try {
          trackEvent('Subscribe', {
            value: monto,
            currency: 'UYU',
            content_name: datosTarjeta.kitCodigo || 'suscripcion',
            content_ids: datosTarjeta.kitCodigo ? [datosTarjeta.kitCodigo] : [],
            predicted_ltv: monto * 12, // proxy de LTV anual, ayuda al optimizador
          })
          trackEvent('Purchase', {
            value: monto,
            currency: 'UYU',
            content_type: 'product',
            content_ids: datosTarjeta.kitCodigo ? [datosTarjeta.kitCodigo] : [],
          })
        } catch (pixelErr) {
          // No bloqueamos el flujo si el pixel falla
          console.warn('Meta Pixel Subscribe/Purchase falló:', pixelErr)
        }
        navigate('/cliente/mi-plan?preapproval=ok', { replace: true })
        return
      }
      // Si no quedó authorized (edge case), caemos a init_point si vino
      if (resp?.init_point) {
        window.location.href = resp.init_point
        return
      }
      throw new Error(resp?.error || 'No se pudo autorizar la suscripción')
    } catch (e) {
      // P0 antifraude (item 3): si MP rechazó por seguridad (high_risk), bloqueamos el reintento
      // inmediato con la misma compra 15 min y sugerimos transferencia. El código real de MP viene
      // en e.mpCode; como respaldo miramos el status_detail del detalle.
      const detalleMP = String(e?.mpCode || e?.mpDetail?.status_detail || e?.mpDetail || '').toLowerCase()
      if (detalleMP.includes('high_risk')) {
        setHighRiskUntil(Date.now() + 15 * 60 * 1000)
        setErr('MP nos pide esperar antes de reintentar por seguridad. Probá con otro medio de pago (transferencia bancaria) o esperá 15 minutos.')
      } else {
        setErr(e.message)
      }
      setCreando(false)
    }
  }

  // ---- Handlers del flujo nuevo (Secure Fields 3DS · flag payment_3ds) ----
  function trackSuscPixels() {
    const monto = Number(datosTarjeta?.monto || 0)
    try {
      trackEvent('Subscribe', { value: monto, currency: 'UYU', content_name: datosTarjeta?.kitCodigo || 'suscripcion', content_ids: datosTarjeta?.kitCodigo ? [datosTarjeta.kitCodigo] : [], predicted_ltv: monto * 12 })
      trackEvent('Purchase', { value: monto, currency: 'UYU', content_type: 'product', content_ids: datosTarjeta?.kitCodigo ? [datosTarjeta.kitCodigo] : [] })
    } catch (e) { console.warn('Meta Pixel Subscribe/Purchase falló:', e) }
  }
  function onSuscActivada() {
    trackSuscPixels()
    try { localStorage.removeItem(STORAGE_KEY) } catch {}
    navigate('/cliente/mi-plan?preapproval=ok', { replace: true })
  }
  function onSuscFallo() {
    // Pagó el mes1 pero el preapproval quedó en cola de activación (admin confirma por WhatsApp).
    // El pago mes1 es real → igual disparamos los pixels de conversión.
    trackSuscPixels()
    try { localStorage.removeItem(STORAGE_KEY) } catch {}
    navigate('/cliente/mi-plan?preapproval=pendiente', { replace: true })
  }

  async function logout() {
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  const nombre = profile?.nombre?.split(' ')[0] || ''
  const totalPasos = 5  // 0..4

  // Sub-fase 4.3: sin user ya NO redirigimos — el wizard es público hasta el paso 4,
  // donde <SignupInline> pide crear cuenta antes de pagar (ver render del paso 4).

  return (
    <div className="lp-app">
      <ScrollHintPill />
      {/* NAVBAR estilo web pública */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <Link to={user ? '/cliente' : '/'} className="lp-logo">Lim<span>pack</span></Link>
          <div className="lp-nav-actions">
            {user ? (
              <>
                <Link to="/cliente" className="lp-nav-link">← Mi cuenta</Link>
                <button onClick={logout} className="lp-nav-logout">Salir</button>
              </>
            ) : (
              // Visitante público (Sub-fase 4.3): ofrecer ingreso a clientes existentes.
              <Link to={`/login?next=${encodeURIComponent('/suscribirme')}`} className="lp-nav-link">Ya tengo cuenta →</Link>
            )}
          </div>
        </div>
      </nav>

      {/* HERO degradé igual que la web */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-pill">
            <span className="lp-hero-pill-dot"></span>
            Paso {paso + 1} de {totalPasos} · Crear suscripción
          </div>
          <h1 className="lp-hero-h1">
            {paso === 0 && <>Armemos tu kit, <span>{nombre || 'hola'}</span></>}
            {paso === 1 && <>Elegí <span>tu kit</span></>}
            {paso === 2 && <>¿A dónde te lo <span>llevamos?</span></>}
            {paso === 3 && <>¿Cuándo te queda <span>mejor?</span></>}
            {paso === 4 && <>Confirmá tu <span>suscripción</span></>}
          </h1>
          <p className="lp-hero-sub">
            {paso === 0 && 'Primero contanos cómo es tu hogar — así te recomendamos el kit ideal.'}
            {paso === 1 && segmentoElegido && `Te mostramos los kits para ${segmentoElegido.titulo.toLowerCase()}.`}
            {paso === 2 && 'Dirección y barrio. Tenemos ruta fija — el envío es gratis.'}
            {paso === 3 && barrioElegido && `En ${barrioElegido.nombre} repartimos los días ${diasDisponibles.join(', ')}.`}
            {paso === 4 && 'Si está todo bien, creamos tu suscripción y te avisamos por WhatsApp.'}
          </p>
        </div>
      </section>

      {/* Banner: avance retomado */}
      {reanudadoAvisoVisible && (
        <div className="lp-banner-resume">
          <div className="lp-banner-resume-inner">
            <span className="lp-banner-resume-icon">💾</span>
            <div className="lp-banner-resume-txt">
              <strong>Retomamos donde te habías quedado.</strong> Si querés arrancar
              de cero, podés empezar de nuevo.
            </div>
            <button type="button" className="lp-banner-resume-btn" onClick={limpiarWizard}>
              Empezar de nuevo
            </button>
            <button type="button" className="lp-banner-resume-close" onClick={() => setReanudadoAvisoVisible(false)} aria-label="Cerrar">×</button>
          </div>
        </div>
      )}

      {/* Banner: kit ya seleccionado desde /tienda */}
      {kitDesdeTiendaVisible && kitElegido && (
        <div className="lp-banner-resume" style={{ background: 'rgba(0,168,181,.08)', borderColor: 'rgba(0,168,181,.3)' }}>
          <div className="lp-banner-resume-inner">
            <span className="lp-banner-resume-icon">🎯</span>
            <div className="lp-banner-resume-txt">
              <strong>Kit {kitElegido.nombre} {TIER_LABEL[kitElegido.tier] || ''}</strong> · $ {Number(kitElegido.precio_susc).toLocaleString('es-UY')}/mes.
              Ya lo elegiste en la tienda — completá dirección y día para terminar.
            </div>
            <button type="button" className="lp-banner-resume-btn" onClick={() => { setPaso(1); setKitDesdeTiendaVisible(false) }}>
              Cambiar kit
            </button>
            <button type="button" className="lp-banner-resume-close" onClick={() => setKitDesdeTiendaVisible(false)} aria-label="Cerrar">×</button>
          </div>
        </div>
      )}

      {/* Banner: envío gratis + barrios del día */}
      <BannerRepartoHoy barrios={barrios} />

      {/* Stepper */}
      <div className="lp-stepper-wrap">
        <div className="lp-stepper">
          {['Hogar', 'Kit', 'Dirección', 'Día', 'Confirmar'].map((label, i) => (
            <div key={i} className={`lp-step ${paso === i ? 'active' : ''} ${paso > i ? 'done' : ''}`}>
              <div className="lp-step-num">{paso > i ? '✓' : i + 1}</div>
              <div className="lp-step-label">{label}</div>
            </div>
          ))}
        </div>
      </div>

      <main className="lp-main">

        {/* PASO 0: Segmentar por hogar */}
        {paso === 0 && (
          <div className="lp-grid-segs">
            {SEGMENTOS.map(s => {
              const elegido = form.segmento === s.id
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`lp-seg-card ${elegido ? 'is-elegido' : ''}`}
                  onClick={() => setForm({ ...form, segmento: s.id, kitCodigo: '' })}
                >
                  <div className="lp-seg-icon">{s.icono}</div>
                  <div className="lp-seg-titulo">{s.titulo}</div>
                  <div className="lp-seg-sub">{s.sub}</div>
                  <div className="lp-seg-det">{s.detalle}</div>
                  {elegido && <div className="lp-seg-check">✓ Seleccionado</div>}
                </button>
              )
            })}
            <BtnNav
              onNext={() => {
                // Fix 7-jul-2026 (Nico): Comercio/oficina es B2B → NO seguir flow
                // residencial. Redirigir a /cotizar que crea una consulta_comercial
                // y el equipo Limpack contacta para armar propuesta.
                if (form.segmento === 'comercio') {
                  navigate('/cotizar?desde=wizard')
                  return
                }
                setPaso(1)
                setTierActivo('essential')
              }}
              disabled={!form.segmento}
            />
          </div>
        )}

        {/* PASO 1: Kits filtrados con toggle Essential/Premium */}
        {paso === 1 && (
          <div className="lp-card lp-card-pad">
            {/* Toggle solo si NO es comercio y tenemos un segmento */}
            {form.segmento !== 'comercio' && segmentoElegido && !verTodos && (
              <>
                <div className="lp-tier-info">
                  <strong>Essential</strong>: máximo ahorro con calidad comprobada.<br/>
                  <strong>Premium</strong>: marcas reconocidas y aromas que se sienten — más rendimiento y confort.
                </div>
                <div className="lp-toggle-wrap">
                  <div className="lp-toggle">
                    <button className={tierActivo === 'essential' ? 'on' : ''} onClick={() => setTierActivo('essential')}>ESSENTIAL</button>
                    <button className={tierActivo === 'premium' ? 'on' : ''} onClick={() => setTierActivo('premium')}>PREMIUM</button>
                  </div>
                </div>
              </>
            )}

            {/* Lista de kits */}
            <div className="lp-kits">
              {kitsVisibles.map(k => {
                const elegido = form.kitCodigo === k.codigo
                const expandido = kitExpandido === k.codigo
                const esPremium = k.tier === 'premium'
                const sinStock = k.sin_stock === true
                return (
                  <div key={k.codigo} className={`lp-kit ${elegido ? 'is-elegido' : ''} ${esPremium ? 'is-premium' : ''} ${sinStock ? 'is-sin-stock' : ''}`}>
                    {sinStock ? (
                      <div className="lp-kit-badge is-warning">😔 Sin stock esta semana</div>
                    ) : k.destacado && (
                      <div className="lp-kit-badge">⭐ Más popular</div>
                    )}
                    <div className="lp-kit-head">
                      <div className="lp-kit-titulo">
                        <h3>{k.nombre}</h3>
                        <span className={`lp-kit-tier tier-${k.tier}`}>{TIER_LABEL[k.tier] || k.tier}</span>
                      </div>
                      <div className="lp-kit-meta">
                        {k.personas} · {k.productos.filter(p => !p.es_regalo).length} productos
                        {k.productos.some(p => p.es_regalo) && <span style={{ color: '#5BBDD8', fontWeight: 700 }}> + 🎁 regalo del mes</span>}
                      </div>
                      {k.descripcion && <p className="lp-kit-desc">{k.descripcion}</p>}
                    </div>

                    <div className="lp-kit-precio-row">
                      <div>
                        <div className="lp-kit-precio">${Number(k.precio_susc).toLocaleString('es-UY')}</div>
                        <div className="lp-kit-precio-sub">por mes · envío incluido</div>
                      </div>
                      <button type="button" className="lp-kit-ver" onClick={() => setKitExpandido(expandido ? null : k.codigo)}>
                        {expandido ? 'Ocultar productos ▴' : 'Ver qué incluye ▾'}
                      </button>
                    </div>

                    {expandido && (
                      <ul className="lp-kit-productos">
                        {consolidarProductosKit(k.productos.filter(p => !p.es_regalo)).map((p, i) => (
                          <li key={i}>
                            {!p.es_consolidado && <span className="lp-kit-prod-cant">{p.cantidad}×</span>}
                            <span className="lp-kit-prod-nom">{p.nombre}</span>
                            {(() => {
                              const pres = p.es_consolidado ? p.presentacion : presentacionTotal(p.cantidad, p.presentacion)
                              return pres ? <span className="lp-kit-prod-pres"> · {pres}</span> : null
                            })()}
                            {!p.es_consolidado && p.marca && <span className="lp-kit-prod-marca"> · {p.marca}</span>}
                          </li>
                        ))}
                        {k.productos.some(p => p.es_regalo) && (
                          <li style={{ background: 'linear-gradient(90deg, #fffbeb, #fef3c7)', padding: '6px 10px', borderRadius: 6, marginTop: 4, border: '1px dashed #5BBDD8' }}>
                            <span style={{ fontSize: 16 }}>🎁</span>
                            <span className="lp-kit-prod-nom" style={{ color: '#78350f', fontWeight: 700 }}> Regalo sorpresa del mes</span>
                            <span className="lp-kit-prod-pres" style={{ color: '#92400e' }}> · cambia cada mes, distinto cada vez</span>
                          </li>
                        )}
                      </ul>
                    )}

                    {sinStock ? (
                      <a
                        href={`https://wa.me/59898610001?text=${encodeURIComponent(`Hola! Quiero que me avisen cuando vuelva a haber stock del ${k.nombre} 🙌`)}`}
                        target="_blank" rel="noopener"
                        className="lp-kit-btn-wa"
                      >
                        🔔 Avisame por WhatsApp cuando vuelva
                      </a>
                    ) : (
                      <button
                        type="button"
                        className={elegido ? 'lp-kit-btn-on' : 'lp-kit-btn'}
                        onClick={() => setForm({ ...form, kitCodigo: k.codigo })}
                      >
                        {elegido ? '✓ Este es mi kit' : 'Quiero este kit'}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Toggle ver todos los kits */}
            <button type="button" className="lp-link-todos" onClick={() => setVerTodos(v => !v)}>
              {verTodos ? '← Volver a las recomendaciones' : 'Quiero ver todos los kits disponibles →'}
            </button>

            <BtnNav
              onBack={() => { setPaso(0); setKitExpandido(null) }}
              onNext={() => setPaso(2)}
              disabled={!form.kitCodigo}
            />
          </div>
        )}

        {/* PASO 2: Dirección */}
        {paso === 2 && (
          <div className="lp-card lp-card-pad">
            {direccionPrellenada && (form.direccion || form.barrioId) && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', marginBottom: 14,
                background: 'rgba(0,168,181,.08)',
                border: '1px solid rgba(0,168,181,.28)',
                borderRadius: 10, fontSize: 13, color: '#334155', lineHeight: 1.45,
              }}>
                <span style={{ fontSize: 18 }}>📍</span>
                <div style={{ flex: 1 }}>
                  Usamos <strong>tu dirección guardada</strong> — revisá que esté bien y avanzá. Si querés otra, editá los campos.
                </div>
                <button type="button" onClick={() => {
                  setForm(f => ({ ...f, direccion: '', apto: '', barrioId: '' }))
                  setCoords(null); setCoordsExactas(false)
                  setDireccionPrellenada(false)
                }} style={{ background: 'none', border: '1px solid rgba(0,168,181,.28)', color: '#0891b2', fontWeight: 700, fontSize: 12, cursor: 'pointer', padding: '5px 10px', borderRadius: 6 }}>
                  Limpiar
                </button>
              </div>
            )}

            <Field label="Alias (opcional)">
              <input type="text" value={form.alias} onChange={e => setForm({ ...form, alias: e.target.value })} placeholder="Casa, oficina, mamá…" />
            </Field>

            <div className="lp-row-2">
              <Field label="Barrio" required>
                <select value={form.barrioId} onChange={e => setForm({ ...form, barrioId: e.target.value, diaEntrega: '' })}>
                  <option value="">— elegir —</option>
                  {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
              </Field>
              <Field label="Tipo de vivienda">
                <select value={form.tipoVivienda} onChange={e => setForm({ ...form, tipoVivienda: e.target.value })}>
                  <option value="">— elegir —</option>
                  <option value="casa">Casa</option>
                  <option value="edificio">Edificio</option>
                  <option value="oficina">Oficina</option>
                </select>
              </Field>
            </div>

            {!form.barrioId && barrios.length > 0 && (
              <div className="lp-info">
                Por ahora cubrimos: {barrios.map(b => b.nombre).join(', ')}.
              </div>
            )}

            {/* Fix 7-jul-2026 (Nico): separado en 3 campos (Calle · Número · Apto).
                Antes el input único permitía avanzar con solo la calle sin número.
                El repartidor no puede llegar sin número exacto. */}
            <div className="lp-row-calle-nro">
              <Field label="Calle" required>
                <input type="text" required value={form.calle}
                  onChange={e => {
                    const calle = e.target.value
                    setForm(f => ({ ...f, calle, direccion: `${calle.trim()} ${(f.numero || '').trim()}`.trim() }))
                  }}
                  onBlur={() => { if (form.calle && form.numero) geocodear() }}
                  placeholder="Ej: Av. Brasil" />
              </Field>
              <Field label="Número" required>
                <input type="text" required inputMode="numeric" value={form.numero}
                  onChange={e => {
                    const numero = e.target.value
                    setForm(f => ({ ...f, numero, direccion: `${(f.calle || '').trim()} ${numero.trim()}`.trim() }))
                  }}
                  onBlur={() => { if (form.calle && form.numero) geocodear() }}
                  placeholder="2500" />
              </Field>
              <Field label="Apto / Piso">
                <input type="text" value={form.apto} onChange={e => setForm({ ...form, apto: e.target.value })} placeholder="Ej: 501" />
              </Field>
            </div>
            <style>{`
              .lp-row-calle-nro { display: grid; grid-template-columns: 3fr 1fr 1fr; gap: 12px }
              @media (max-width: 520px) {
                .lp-row-calle-nro { grid-template-columns: 1fr 1fr }
                .lp-row-calle-nro > *:first-child { grid-column: 1 / -1 }
              }
            `}</style>

            <button type="button" onClick={geocodear} disabled={!form.barrioId || geocodando} className="lp-btn-secondary">
              {geocodando ? 'Buscando…' : coords ? 'Recargar mapa' : 'Verificar en mapa'}
            </button>

            {/* Validación: barrio detectado vs barrio elegido */}
            {validacionBarrio && !validacionBarrio.ok && (
              <div className={`lp-warn-barrio ${validacionBarrio.sinCobertura ? 'is-bloqueo' : 'is-warn'}`}>
                <div className="lp-warn-barrio-icon">{validacionBarrio.sinCobertura ? '🚫' : '⚠️'}</div>
                <div className="lp-warn-barrio-body">
                  <strong>{validacionBarrio.mensaje}</strong>
                  {validacionBarrio.sinCobertura ? (
                    <>
                      <p>Por ahora no llegamos a esa zona. Si querés que te avisemos cuando lleguemos:</p>
                      <a href={`https://wa.me/59898610001?text=${encodeURIComponent('Hola Limpack! Vivo en ' + form.direccion + ' y todavía no llegan a mi zona. ¿Me avisan cuando empiecen?')}`}
                         target="_blank" rel="noopener" className="lp-warn-cta">
                        📲 Avisame por WhatsApp
                      </a>
                    </>
                  ) : validacionBarrio.sugerenciaActivo ? (
                    <>
                      <p>Para continuar, cambiá el barrio elegido o corregí la dirección.</p>
                      <button type="button" className="lp-warn-cta" onClick={aplicarSugerenciaBarrio}>
                        ✓ Cambiar a {validacionBarrio.sugerenciaActivo.nombre}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {coords && !validacionBarrio?.sinCobertura && (
              <>
                <div style={{ marginTop: 12 }}>
                  <MapPin lat={coords.lat} lng={coords.lng} onChange={(lat, lng) => { setCoords({ lat, lng }); setCoordsExactas(false) }} height={220} />
                </div>
                <label className={`lp-confirm-pin ${coordsExactas ? 'ok' : 'pend'}`}>
                  <input type="checkbox" checked={coordsExactas} onChange={e => setCoordsExactas(e.target.checked)} />
                  <span>{coordsExactas ? 'Pin en la ubicación correcta' : 'Confirmá el pin antes de seguir'}</span>
                </label>
              </>
            )}

            {/* Hint visible si todavía no verificó en mapa — guía al usuario */}
            {!coords && form.barrioId && form.direccion.trim() && !validacionBarrio?.sinCobertura && (
              <div className="lp-info" style={{ marginTop: 12, background: '#fef3c7', borderColor: '#f59e0b', color: '#78350f' }}>
                📍 Verificá la dirección en el mapa antes de continuar.
              </div>
            )}

            <BtnNav
              onBack={() => setPaso(1)}
              onNext={() => setPaso(3)}
              disabled={
                !form.barrioId
                || !(form.calle || '').trim()    // Fix 7-jul-2026: calle y número por separado (guarda contra undefined si localStorage viejo)
                || !(form.numero || '').trim()
                || (validacionBarrio && !validacionBarrio.ok)
                || !coords                                           // SIEMPRE obligatorio verificar dirección en mapa
                || (!validacionBarrio?.sinCobertura && !coordsExactas)  // y confirmar pin
              } />
          </div>
        )}

        {/* PASO 3: Día */}
        {paso === 3 && (
          <div className="lp-card lp-card-pad">
            {/* DÍA DE ENTREGA */}
            {diasDisponibles.length === 0 ? (
              <div className="lp-info" style={{ background: '#fef3c7', borderColor: '#f59e0b', color: '#78350f' }}>
                ⚠️ Este barrio no tiene días de reparto cargados. Volvé a Dirección y elegí otro.
              </div>
            ) : diasDisponibles.length === 1 ? (
              <Field label={`Día de entrega en ${barrioElegido?.nombre || 'tu barrio'}`}>
                <div style={{ padding: '12px 14px', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 10, color: '#065f46', fontSize: 14 }}>
                  🚚 Pasamos por <strong>{barrioElegido?.nombre}</strong> los <strong style={{ textTransform: 'capitalize' }}>{diasDisponibles[0]}</strong>.
                  <div style={{ fontSize: 12, marginTop: 4, color: '#047857' }}>Ese es el día fijo de tu entrega mensual.</div>
                </div>
              </Field>
            ) : (
              // 8-jul-2026: reemplazado el toggle interactivo por bloque informativo.
              // El día de la semana se auto-setea al elegir la primera fecha abajo (línea ~907).
              // El toggle era redundante y podía contradecir la fecha elegida.
              <Field label={`Días que pasamos por ${barrioElegido?.nombre || 'tu barrio'}`}>
                <div style={{ padding: '12px 14px', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 10, color: '#065f46', fontSize: 14 }}>
                  🚚 En <strong>{barrioElegido?.nombre}</strong> pasamos los <strong style={{ textTransform: 'capitalize' }}>{diasDisponibles.join(' y ')}</strong>.
                  <div style={{ fontSize: 12, marginTop: 4, color: '#047857' }}>
                    Tu día fijo mensual se define automático con la fecha de primera entrega que elijas abajo.
                  </div>
                </div>
              </Field>
            )}

            {/* PRIMERA ENTREGA — nuevo 8-jul-2026 (task #36):
                Cliente elige fecha directa de las próximas 6 fechas del barrio.
                Auto-seleccionamos la más pronto disponible. Después arranca el ciclo mensual MP. */}
            {diasDisponibles.length > 0 && (() => {
              const fechas = proximasFechasDelBarrio(diasDisponibles, 6)
              // Auto-seleccionar la primera fecha si no hay ninguna elegida
              // (también setea diaEntrega con el día de la semana de esa fecha)
              if (fechas.length > 0 && !form.primeraFechaISO) {
                const DIAS_ES = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado']
                const primeraFecha = fechas[0]
                const iso = primeraFecha.toISOString().slice(0, 10)
                const diaSemana = DIAS_ES[primeraFecha.getDay()]
                setTimeout(() => setForm(prev => prev.primeraFechaISO ? prev : {
                  ...prev,
                  primeraFechaISO: iso,
                  diaEntrega: diaSemana,
                }), 0)
              }

              // Detecta días del barrio ENTRE hoy y la primera opción visible que
              // quedaron filtrados por corte D-1 16hs pasado. Ej: son las 23hs miércoles
              // → jueves quedó filtrado (su corte era hoy 16hs). Le explicamos al cliente
              // qué día(s) cercano(s) no aparecen y por qué, sin ser condescendientes.
              const diasCortados = (() => {
                if (fechas.length === 0) return []
                const primeraOpcion = fechas[0]
                const hoyDate = new Date()
                hoyDate.setHours(0, 0, 0, 0)
                const DIAS_LABEL = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
                const DIAS_MATCH = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado']
                const norm = s => String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
                const targets = new Set(diasDisponibles.map(d => DIAS_MATCH.indexOf(norm(d))).filter(i => i >= 0))
                const cortados = []
                for (let offset = 1; offset < 8; offset++) {
                  const d = new Date(hoyDate)
                  d.setDate(d.getDate() + offset)
                  if (d >= primeraOpcion) break
                  if (targets.has(d.getDay())) {
                    cortados.push(`${DIAS_LABEL[d.getDay()]} ${d.getDate()}`)
                  }
                }
                return cortados
              })()

              return (
                <Field
                  label="¿Cuándo querés tu primera entrega?"
                  required
                  hint={`Elegí una fecha. Ese día es tu primera entrega y el cobro arranca hoy al confirmar; los próximos van cada mes, con entrega el día del barrio (${diasDisponibles.join(' / ')}) más cercano a cada cobro.`}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
                    {fechas.map((fecha, idx) => {
                      const iso = fecha.toISOString().slice(0, 10)
                      const elegida = form.primeraFechaISO === iso
                      const fechaTxt = fecha.toLocaleDateString('es-UY', { weekday: 'short', day: 'numeric', month: 'short' })
                      const esLaMasProxima = idx === 0
                      return (
                        <button
                          key={iso}
                          type="button"
                          onClick={() => {
                            // Al elegir fecha, autoseteamos también diaEntrega
                            // (día de la semana de esa fecha, en español)
                            const DIAS_ES = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado']
                            const diaSemana = DIAS_ES[fecha.getDay()]
                            setForm({ ...form, primeraFechaISO: iso, diaEntrega: diaSemana })
                          }}
                          style={{
                            padding: '12px 14px',
                            borderRadius: 12,
                            border: elegida ? '2px solid #00A8B5' : '1px solid #e2e8f0',
                            background: elegida ? '#ecfeff' : '#fff',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            transition: 'all .15s',
                            position: 'relative',
                          }}
                        >
                          {esLaMasProxima && (
                            <div style={{
                              position: 'absolute', top: -8, right: 8,
                              background: '#10b981', color: '#fff', fontSize: 10, fontWeight: 800,
                              padding: '2px 8px', borderRadius: 999, letterSpacing: '.04em', textTransform: 'uppercase',
                            }}>
                              Más pronto
                            </div>
                          )}
                          <div style={{ fontSize: 14, fontWeight: 800, color: '#1A3461', textTransform: 'capitalize' }}>
                            {elegida && '✓ '}{fechaTxt}
                          </div>
                          <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
                            Cobro inmediato
                          </div>
                        </button>
                      )
                    })}
                  </div>
                  {diasCortados.length > 0 && (
                    <div style={{ marginTop: 10, padding: '10px 12px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, fontSize: 12, color: '#78350F', lineHeight: 1.5 }}>
                      ⏰ {diasCortados.length === 1 ? 'El' : 'Los'} <strong style={{ textTransform: 'capitalize' }}>{diasCortados.join(' y el ')}</strong> no {diasCortados.length === 1 ? 'aparece' : 'aparecen'} porque ya pasó nuestro corte de armado (día anterior a las 16hs). El próximo día disponible es <strong style={{ textTransform: 'capitalize' }}>{fechas[0].toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })}</strong>.
                    </div>
                  )}
                </Field>
              )
            })()}

            <Field label="Ventana horaria">
              <select value={form.ventana} onChange={e => setForm({ ...form, ventana: e.target.value })}>
                {VENTANAS_HORARIAS.map(v => (
                  <option key={v.key} value={v.key}>{v.label}</option>
                ))}
              </select>
            </Field>

            <div className="lp-info" style={{ marginTop: 16 }}>
              💳 <strong>Pagás al confirmar y recibís tu primer kit en la próxima pasada por {barrioElegido?.nombre || 'tu barrio'}.</strong> Después, cada mes: MercadoPago cobra automático y nosotros te entregamos el día de {barrioElegido?.nombre || 'tu barrio'} ({diasDisponibles.join(' / ') || 'día del barrio'}) más cercano al cobro.
            </div>

            <Field label="Notas para el repartidor (opcional)">
              <textarea value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} rows="2" placeholder="Llamar al timbre, portero hasta las 18hs, etc." />
            </Field>

            <BtnNav onBack={() => setPaso(2)} onNext={() => setPaso(4)} disabled={!form.primeraFechaISO} />
          </div>
        )}

        {/* PASO 4: Confirmar */}
        {paso === 4 && (
          <div className="lp-card lp-card-pad">
            <div className="lp-resumen">
              <Row label="Hogar">{segmentoElegido?.titulo} · {segmentoElegido?.sub}</Row>
              <Row label="Kit">
                <strong>{kitElegido?.nombre}</strong> · ${Number(kitElegido?.precio_susc).toLocaleString('es-UY')} / mes
                {/* La nota de "promedio con bonificación" es SOLO del flujo legacy (ladder 3 meses).
                    En el flujo plano (payment_3ds) el precio es precio_susc fijo todos los meses. */}
                {flujoMes1 !== 'payment_3ds' && kitElegido?.precio_unica && Number(kitElegido.precio_unica) > Number(kitElegido.precio_susc) && (
                  <small style={{ color: '#64748b', display: 'block', marginTop: 2 }}>
                    Promedio desde el mes 1 (con bonificación 3 meses · ver abajo)
                  </small>
                )}
              </Row>
              <Row label="Dirección">{barrioElegido?.nombre} · {form.direccion}{form.apto ? ` · ${form.apto}` : ''}</Row>
              <Row label="Días del barrio">
                <strong style={{ textTransform: 'capitalize' }}>{diasDisponibles.join(' y ')}</strong> · {labelVentana(form.ventana).toLowerCase()}
              </Row>
              {form.primeraFechaISO && (
                <Row label="Primera entrega">
                  <strong style={{ textTransform: 'capitalize' }}>{
                    new Date(form.primeraFechaISO + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
                  }</strong>
                </Row>
              )}
              {form.primeraFechaISO && (
                <Row label="Primer cobro">
                  <strong style={{ textTransform: 'capitalize' }}>Hoy</strong>
                  {' '}— al confirmar ahora
                  {flujoMes1 === 'payment_3ds' ? (
                    // Precio plano: el mes 1 se cobra igual que los siguientes (precio_susc).
                    <div style={{ marginTop: 4, fontSize: 13 }}>
                      Monto: <strong>${Number(kitElegido?.precio_susc).toLocaleString('es-UY')}</strong>{' '}
                      <small style={{ color: '#64748b' }}>(igual todos los meses)</small>
                    </div>
                  ) : (kitElegido?.precio_unica && Number(kitElegido.precio_unica) > Number(kitElegido.precio_susc) && (
                    <div style={{ marginTop: 4, fontSize: 13 }}>
                      Monto: <strong>${Number(kitElegido.precio_unica).toLocaleString('es-UY')}</strong>{' '}
                      <small style={{ color: '#64748b' }}>(con bonificación de los meses 2 y 3, promedio ${Number(kitElegido.precio_susc).toLocaleString('es-UY')}/mes)</small>
                    </div>
                  ))}
                </Row>
              )}
              <Row label="Cobros siguientes">
                Cada mes MercadoPago cobra automático. Entrega el día del barrio más cercano al cobro.
              </Row>
              {form.alias && <Row label="Alias">{form.alias}</Row>}
              {form.notas && <Row label="Notas">{form.notas}</Row>}
            </div>

            {/* Sub-fase 4.3: el resumen de arriba es público. El pago requiere cuenta: si el
                visitante no tiene sesión mostramos <SignupInline> (crear cuenta por email) en vez
                de la facturación + Brick. Logueado (o de vuelta tras activar) → flujo de siempre. */}
            {authLoading ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 14 }}>Verificando tu sesión…</div>
            ) : !user ? (
              <SignupInline kitNombre={kitElegido?.nombre} monto={flujoMes1 === 'payment_3ds' ? (kitElegido?.precio_susc || kitElegido?.precio_unica) : (kitElegido?.precio_unica || kitElegido?.precio_susc)} />
            ) : (
            <>
            {/* Facturación: obligatorio antes de confirmar */}
            <div style={{ marginBottom: 14 }}>
              <BloqueFacturacion
                value={perfilFacturacionId}
                onChange={setPerfilFacturacionId}
              />
            </div>

            {/* Breakdown de la política de 3 meses (ladder) — SOLO flujo legacy.
                En el flujo plano (payment_3ds) el precio es precio_susc fijo todos los meses,
                así que este recuadro NO aplica y se oculta. */}
            {flujoMes1 !== 'payment_3ds' && kitElegido && (() => {
              const precioUnica = Number(kitElegido.precio_unica || kitElegido.precio_susc)
              const precioSusc = Number(kitElegido.precio_susc)
              const mes1 = precioUnica
              const mes2y3 = Math.round((3 * precioSusc - precioUnica) / 2)
              const devolucion = mes1 - mes2y3
              const promedio = precioSusc
              const fmt = (n) => `$${Number(n).toLocaleString('es-UY')}`
              const aplica = precioUnica > precioSusc
              if (!aplica) return null

              return (
                <div style={{
                  border: '1px solid #C5E0EA', borderRadius: 12, padding: '14px 16px',
                  background: 'linear-gradient(135deg, #f0f9ff 0%, #ecfeff 100%)',
                  marginBottom: 12,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 18 }}>🎁</span>
                    <strong style={{ color: '#0F4F7A', fontSize: 14 }}>Cómo va tu suscripción los primeros 3 meses</strong>
                  </div>
                  <div style={{ display: 'grid', gap: 6, fontSize: 13.5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#fff', borderRadius: 6, border: '1px solid #e0f2fe' }}>
                      <span><strong>Mes 1</strong> · primer cobro</span>
                      <strong style={{ color: '#1A3461' }}>{fmt(mes1)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#fff', borderRadius: 6, border: '1px solid #e0f2fe' }}>
                      <span><strong>Mes 2</strong> · te devolvemos {fmt(devolucion)}</span>
                      <strong style={{ color: '#16a34a' }}>{fmt(mes2y3)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#fff', borderRadius: 6, border: '1px solid #e0f2fe' }}>
                      <span><strong>Mes 3</strong> · te devolvemos {fmt(devolucion)}</span>
                      <strong style={{ color: '#16a34a' }}>{fmt(mes2y3)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#ecfdf5', borderRadius: 6, border: '1px solid #a7f3d0' }}>
                      <span><strong>Mes 4 en adelante</strong> · precio susc permanente</span>
                      <strong style={{ color: '#065f46' }}>{fmt(precioSusc)} <small style={{ fontWeight: 400 }}>/mes</small></strong>
                    </div>
                  </div>
                  <div style={{ marginTop: 10, padding: '8px 10px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12, color: '#78350f' }}>
                    💡 <strong>Promedio mensual: {fmt(promedio)}</strong> desde el primer mes. El mes 1 viene más alto porque la bonificación se devuelve en los meses 2 y 3 si completás los 3 meses. Si cancelás antes, no pasa nada — solo pagaste 1 o 2 meses al precio que viste.
                  </div>
                </div>
              )
            })()}

            {!datosTarjeta && (
              <div className="lp-info-pago">
                <strong>💳 Cómo se paga</strong><br/>
                Al confirmar, cargás tu tarjeta acá mismo — <strong>proceso 100% seguro con MercadoPago</strong>. El primer cobro se hace ahora al confirmar
                {flujoMes1 === 'payment_3ds'
                  ? <> — <strong>igual que los cobros siguientes</strong>. La tarjeta queda autorizada para los meses que vienen.</>
                  : <>; la tarjeta queda autorizada para los cobros mensuales siguientes.</>}
                <br/><br/>
                Pagás ahora al confirmar. Después, todos los meses MercadoPago cobra automático. Si falla algún cobro, te avisamos por WhatsApp antes de que salga Limpack para tu casa. Sin permanencia, cancelás cuando quieras.
              </div>
            )}

            {err && <div className="lp-err">{err}</div>}

            {!datosTarjeta ? (
              <BtnNav
                onBack={() => setPaso(3)}
                onNext={confirmar}
                nextLabel={creando ? 'Creando…' : '✓ Confirmar y crear suscripción'}
                disabled={creando || !perfilFacturacionId}
              />
            ) : (() => {
              // Payer completo para MP antifraude: nombre + apellido + doc.
              // Si el profile no tiene documento, primero el mini-form pide CI y actualiza profiles.
              const nombreEfectivo = perfilOverride?.nombre || profile?.nombre || ''
              const docEfectivo = perfilOverride?.documento || profile?.documento || ''
              const tipoEfectivo = perfilOverride?.documento ? 'ci' : (profile?.documento_tipo || 'ci')
              const necesitaCompletarPerfil = !docEfectivo || !nombreEfectivo
              if (necesitaCompletarPerfil) {
                return (
                  <MiniFormCompletarDoc
                    nombreInicial={nombreEfectivo}
                    userId={user.id}
                    onListo={(datos) => setPerfilOverride(datos)}
                    onCancelar={() => { setDatosTarjeta(null); setErr(null) }}
                  />
                )
              }
              const { firstName, lastName } = splitNombre(nombreEfectivo)
              const identification = buildIdentificationMP(tipoEfectivo, docEfectivo)
              return (
              <div className="lp-tarjeta-wrap">
                <h3 style={{ margin: '18px 0 6px', fontSize: 17, color: 'var(--cl-navy, #1A3461)' }}>
                  Ingresá los datos de tu tarjeta
                </h3>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748B', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, lineHeight: 1.5 }}>
                  <span>Formulario seguro provisto por</span>
                  <img
                    src="/mp-logo.svg"
                    alt="Mercado Pago"
                    style={{ display: 'block', height: 28, width: 'auto', verticalAlign: 'middle' }}
                  />
                  <span>— tu tarjeta se tokeniza acá mismo, nadie más ve tus datos.</span>
                </p>
                {/* Métodos aceptados: consulta a MP API /v1/payment_methods?marketplace=MLU
                    devolvió estos 5 como estables/comunes en UY para preapproval.
                    Los débito (debvisa, debmaster, debcabal) se colapsan al mismo display. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>Aceptamos</span>
                  <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                    Visa · Mastercard · OCA · American Express · Cabal
                  </span>
                </div>
                {highRiskUntil && highRiskUntil > Date.now() ? (
                  // P0 antifraude (item 3): bloqueo de reintento tras cc_rejected_high_risk.
                  <div style={{ padding: 18, background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 12 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#92400E', marginBottom: 8 }}>🔒 Esperá un momento antes de reintentar</div>
                    <p style={{ fontSize: 13.5, color: '#78350F', lineHeight: 1.55, margin: '0 0 14px' }}>
                      MP nos pide esperar antes de reintentar por seguridad. Probá con <b>otro medio de pago (transferencia bancaria)</b> o esperá 15 minutos y volvé a intentar con la tarjeta.
                    </p>
                    <a
                      href={`https://wa.me/59898610001?text=${encodeURIComponent('Hola! Quiero pagar mi suscripción Limpack por transferencia bancaria (la tarjeta me la rechazó MP por seguridad).')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#25D366', color: '#fff', padding: '12px 20px', borderRadius: 10, fontWeight: 800, fontSize: 14, textDecoration: 'none' }}
                    >
                      💬 Coordinar transferencia bancaria
                    </a>
                    <button
                      type="button"
                      onClick={() => { setHighRiskUntil(null); setErr(null) }}
                      style={{ display: 'block', marginTop: 12, background: 'transparent', border: 0, color: '#92400E', fontSize: 13, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                    >
                      Ya esperé — reintentar con tarjeta
                    </button>
                  </div>
                ) : flujoMes1 === 'payment_3ds' ? (
                  // Flujo nuevo (flag payment_3ds): Secure Fields + doble token + mes1 3DS + leg D.
                  <MpSecureFieldsSusc
                    clienteId={datosTarjeta.clienteId}
                    monto={datosTarjeta.monto}
                    montoMes2={(() => {
                      // Precio PLANO: mes 2+ = precio_susc (igual que mes 1). Sin ladder.
                      const k = kits.find(kk => kk.codigo === datosTarjeta.kitCodigo)
                      return k?.precio_susc != null ? Number(k.precio_susc) : undefined
                    })()}
                    emailPagador={datosTarjeta.email}
                    userId={user?.id}
                    sessionId={sessionIdRef.current}
                    firstName={firstName}
                    lastName={lastName}
                    identification={identification}
                    onActivada={onSuscActivada}
                    onFallo={onSuscFallo}
                    onError={(msg) => setErr(msg)}
                    onHighRisk={() => setHighRiskUntil(Date.now() + 15 * 60 * 1000)}
                  />
                ) : (
                  <MpTarjetaBrick
                    monto={datosTarjeta.monto}
                    emailPagador={datosTarjeta.email}
                    userId={user?.id}
                    sessionId={sessionIdRef.current}
                    firstName={firstName}
                    lastName={lastName}
                    identification={identification}
                    onTokenizado={onTarjetaTokenizada}
                    onError={(msg) => setErr(msg)}
                  />
                )}
                {creando && (
                  <div style={{ marginTop: 12, padding: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, textAlign: 'center', color: '#1e40af', fontSize: 14 }}>
                    Autorizando tu suscripción…
                  </div>
                )}
                <div style={{ marginTop: 14 }}>
                  <button
                    type="button"
                    className="lp-btn-secondary"
                    onClick={() => { setDatosTarjeta(null); setErr(null) }}
                    disabled={creando}
                    style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '10px 18px', borderRadius: 8, color: '#475569', cursor: 'pointer', fontSize: 14 }}
                  >
                    ← Volver a revisar mis datos
                  </button>
                </div>
              </div>
              )
            })()}
            </>
            )}
          </div>
        )}

        {/* Aviso de privacidad — visible en los 4 pasos */}
        <div className="lp-privacy">
          🔒 Tus datos están protegidos. Solo los usamos para procesar tu pedido y nunca los compartimos con terceros.
        </div>
      </main>

      {/* WhatsApp flotante igual que la web */}
      <a href="https://wa.me/59898610001?text=Hola!%20Tengo%20una%20duda%20sobre%20la%20suscripción"
         target="_blank" rel="noopener" className="lp-wa" aria-label="Contactar por WhatsApp">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* FOOTER idéntico a la web pública */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-grid">
            <div>
              <div className="lp-footer-logo">Lim<span>pack</span></div>
              <p className="lp-footer-desc">Productos de limpieza a domicilio por suscripción. Montevideo, Uruguay.</p>
            </div>
            <div>
              <div className="lp-footer-tit">Navegación</div>
              <ul className="lp-footer-list">
                <li><a href="https://limpack.uy/#como-funciona">Cómo funciona</a></li>
                <li><a href="https://limpack.uy/kits.html">Nuestros kits</a></li>
                <li><a href="https://limpack.uy/despensa.html">Despensa Limpack</a></li>
                <li><a href="https://limpack.uy/#zonas">Zonas de entrega</a></li>
              </ul>
            </div>
            <div>
              <div className="lp-footer-tit">Contacto</div>
              <ul className="lp-footer-list">
                <li><a href="https://wa.me/59898610001" target="_blank" rel="noopener">WhatsApp</a></li>
                <li><Link to="/cliente">Mi cuenta</Link></li>
                <li><a href="mailto:hola@limpack.uy">hola@limpack.uy</a></li>
              </ul>
            </div>
          </div>
          <div className="lp-footer-bot">
            <p>© 2026 Limpack SAS · Montevideo, Uruguay · IVA incluido en todos los precios</p>
            <p style={{ opacity: 0.7, fontSize: '.85em', marginTop: 4 }}>Probamos cada producto que te vendemos. El sitio, la app y todo el resto — también lo hacemos nosotros.</p>
          </div>
        </div>
      </footer>

      <style>{styles}</style>
    </div>
  )
}

const DIA_LABEL_MAP = {
  0: 'domingo', 1: 'lunes', 2: 'martes', 3: 'miercoles',
  4: 'jueves', 5: 'viernes', 6: 'sabado',
}

// SignupInline (Sub-fase 4.3) — panel embebido en el paso 4 para visitantes SIN sesión.
// Reusa el signup magic-link EXISTENTE (mismo que Login.jsx modo signup): signInWithOtp con
// shouldCreateUser + Turnstile → manda email "Activá tu cuenta" con emailRedirectTo a
// /activar-cuenta?next=/suscribirme. El user elige contraseña ahí y vuelve al wizard: localStorage
// ('limpack_wizard_susc') restaura el paso 4 ya logueado y termina el pago. No inventa backend nuevo.
function SignupInline({ kitNombre, monto }) {
  const navigate = useNavigate()
  const [nombre, setNombre] = useState('')
  const [apellido, setApellido] = useState('')
  const [email, setEmail] = useState('')
  const [emailConfirm, setEmailConfirm] = useState('')
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [enviado, setEnviado] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (!nombre.trim() || !apellido.trim()) { setError('Ingresá nombre y apellido'); return }
    if (email.trim().toLowerCase() !== emailConfirm.trim().toLowerCase()) {
      setError('Los emails no coinciden — revisá que estén escritos igual'); return
    }
    if (!turnstileToken) { setError('Completá la verificación anti-bot antes de continuar'); return }
    setLoading(true)
    // next=/suscribirme → tras activar la cuenta, /activar-cuenta redirige de vuelta al wizard.
    const emailRedirectTo = `https://panel.limpack.uy/activar-cuenta?next=${encodeURIComponent('/suscribirme')}`
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: {
        shouldCreateUser: true,
        data: { nombre: nombre.trim(), apellido: apellido.trim() },
        emailRedirectTo,
        captchaToken: turnstileToken,
      },
    })
    setLoading(false)
    if (err) {
      const m = err.message || ''
      if (/rate limit/i.test(m)) setError('Demasiados intentos seguidos. Esperá un minuto y probá de nuevo.')
      else setError(m)
      return
    }
    trackEvent('Lead', { content_name: 'signup_wizard_susc_step4' })
    setEnviado(true)
  }

  if (enviado) {
    return (
      <div className="lp-signup-inline">
        <div className="lp-signup-ok">
          <div style={{ fontSize: 40, marginBottom: 8 }}>✉️</div>
          <strong style={{ fontSize: 16 }}>Revisá tu email</strong>
          <p style={{ margin: '8px 0 0', lineHeight: 1.55 }}>
            Te mandamos un link a <strong>{email}</strong>. Hacé click, elegís tu contraseña y
            volvés directo acá para terminar el pago. <strong>Tu avance quedó guardado.</strong>
          </p>
          <p style={{ margin: '10px 0 0', fontSize: 12, color: '#15803d' }}>
            ¿No te llegó en 2 min? Revisá spam o{' '}
            <button type="button" onClick={() => { setEnviado(false); setEmailConfirm('') }}
              style={{ background: 'transparent', border: 0, color: '#15803d', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
              intentá de nuevo
            </button>.
          </p>
        </div>
        <style>{SIGNUP_INLINE_CSS}</style>
      </div>
    )
  }

  return (
    <div className="lp-signup-inline">
      <h3 className="lp-signup-h">Creá tu cuenta para completar</h3>
      <p className="lp-signup-sub">
        {kitNombre
          ? <>Estás a un paso del <strong>{kitNombre}</strong>{monto ? <> · ${Number(monto).toLocaleString('es-UY')}</> : null}. </>
          : null}
        Guardamos todo tu avance. Te mandamos un link por email para activar la cuenta y volvés directo a pagar.
      </p>
      <form onSubmit={handleSubmit} className="lp-signup-form">
        <div className="lp-signup-row">
          <label className="lp-signup-field">
            <b>Nombre</b>
            <input type="text" required autoComplete="given-name" value={nombre}
              onChange={e => setNombre(e.target.value)} placeholder="Juan" />
          </label>
          <label className="lp-signup-field">
            <b>Apellido</b>
            <input type="text" required autoComplete="family-name" value={apellido}
              onChange={e => setApellido(e.target.value)} placeholder="Pérez" />
          </label>
        </div>
        <label className="lp-signup-field">
          <b>Email</b>
          <input type="email" required autoComplete="email" value={email}
            onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" />
        </label>
        <label className="lp-signup-field">
          <b>Confirmá tu email</b>
          <input type="email" required autoComplete="email" value={emailConfirm}
            onChange={e => setEmailConfirm(e.target.value)} placeholder="tu@email.com"
            onPaste={(e) => e.preventDefault()} />
        </label>
        <TurnstileWidget action="signup" onToken={setTurnstileToken} />
        {error && <div className="lp-signup-err">{error}</div>}
        <button type="submit" disabled={loading} className="lp-signup-btn">
          {loading ? 'Enviando link…' : 'Crear cuenta y seguir al pago →'}
        </button>
        <div className="lp-signup-legal">
          Te mandamos un email con un link. Al abrirlo elegís tu contraseña y volvés a esta compra.
        </div>
      </form>
      <button type="button" className="lp-signup-login"
        onClick={() => navigate(`/login?next=${encodeURIComponent('/suscribirme')}`)}>
        ¿Ya tenés cuenta? <strong>Entrar</strong>
      </button>
      <style>{SIGNUP_INLINE_CSS}</style>
    </div>
  )
}

const SIGNUP_INLINE_CSS = `
  .lp-signup-inline {
    border: 1px solid #C5E0EA; border-radius: 14px; padding: 20px 18px; margin-bottom: 12px;
    background: linear-gradient(135deg, #f0f9ff 0%, #ecfeff 100%);
  }
  .lp-signup-h { margin: 0 0 4px; font-size: 18px; font-weight: 900; color: #0F4F7A; letter-spacing: -.01em }
  .lp-signup-sub { margin: 0 0 16px; font-size: 13.5px; color: #475569; line-height: 1.55 }
  .lp-signup-form { display: flex; flex-direction: column; gap: 12px }
  .lp-signup-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
  @media (max-width: 460px) { .lp-signup-row { grid-template-columns: 1fr } }
  .lp-signup-field { display: block }
  .lp-signup-field b {
    display: block; font-size: 11px; font-weight: 800; color: #1a3461;
    letter-spacing: .08em; text-transform: uppercase; margin-bottom: 6px;
  }
  .lp-signup-field input {
    width: 100%; box-sizing: border-box; padding: 12px 14px; font-size: 16px;
    border: 1.5px solid #e2e8f0; border-radius: 10px; background: #fff; color: #1a3461; font-family: inherit;
    transition: border-color .15s, box-shadow .15s;
  }
  .lp-signup-field input:focus { outline: none; border-color: #00A8B5; box-shadow: 0 0 0 3px rgba(0,168,181,.15) }
  .lp-signup-err {
    background: #fef2f2; border: 1px solid #fca5a5; color: #dc2626;
    border-radius: 10px; padding: 10px 14px; font-size: 13px;
  }
  .lp-signup-btn {
    width: 100%; background: linear-gradient(135deg, #1a5cb8 0%, #00A8B5 100%);
    color: #fff; font-weight: 800; font-size: 15px; padding: 14px 20px; border: 0; border-radius: 10px;
    cursor: pointer; font-family: inherit; box-shadow: 0 4px 14px rgba(0,168,181,.25);
    transition: transform .12s, box-shadow .12s, opacity .15s; margin-top: 2px;
  }
  .lp-signup-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(0,168,181,.35) }
  .lp-signup-btn:disabled { opacity: .6; cursor: wait }
  .lp-signup-legal { font-size: 12px; color: #64748b; text-align: center; line-height: 1.5 }
  .lp-signup-login {
    display: block; width: 100%; margin-top: 14px; padding-top: 14px;
    border: 0; border-top: 1px solid rgba(0,168,181,.2); background: transparent;
    color: #64748b; font-size: 13px; cursor: pointer; font-family: inherit; text-align: center;
  }
  .lp-signup-login strong { color: #00A8B5 }
  .lp-signup-ok {
    text-align: center; padding: 20px 8px; color: #15803d;
    background: #f0fdf4; border: 1px solid #86efac; border-radius: 12px;
  }
  .lp-signup-ok strong { color: #166534 }
  .lp-signup-ok p { font-size: 14px; color: #15803d }
`

function BannerRepartoHoy({ barrios }) {
  if (!barrios || barrios.length === 0) return null
  const hoy = DIA_LABEL_MAP[new Date().getDay()]
  const conReparto = barrios.filter(b => Array.isArray(b.dias) && b.dias.includes(hoy))
  return (
    <div className="lp-banner-hoy">
      <div className="lp-banner-hoy-inner">
        <span className="lp-banner-hoy-icon">🚚</span>
        <div className="lp-banner-hoy-txt">
          <strong>Envío gratis en el día de tu barrio.</strong>{' '}
          {conReparto.length > 0
            ? <>Hoy repartimos en: <em>{conReparto.map(b => b.nombre).join(', ')}</em>.</>
            : <>Hoy no hay reparto programado — elegí tu día más cómodo.</>
          }
        </div>
      </div>
      <style>{`
        .lp-banner-hoy {
          max-width: 1100px; margin: 0 auto; padding: 0 16px;
        }
        .lp-banner-hoy-inner {
          background: linear-gradient(135deg, #ecfdf5, #d1fae5);
          border: 1px solid #86efac; border-radius: 12px;
          padding: 10px 14px; display: flex; align-items: center; gap: 10px;
          font-size: 13px; color: #065f46; line-height: 1.45;
        }
        .lp-banner-hoy-icon { font-size: 20px; flex-shrink: 0 }
        .lp-banner-hoy-txt strong { color: #064e3b }
        .lp-banner-hoy-txt em { font-style: normal; font-weight: 700; color: #065f46 }
        @media (min-width: 900px) {
          .lp-banner-hoy { padding: 0 44px }
        }

        /* Banner "retomamos" */
        .lp-banner-resume {
          max-width: 1100px; margin: 0 auto 10px; padding: 0 16px;
        }
        .lp-banner-resume-inner {
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1px solid #93c5fd; border-radius: 12px;
          padding: 10px 14px; display: flex; align-items: center; gap: 10px;
          font-size: 13px; color: #1e3a8a; line-height: 1.4;
        }
        .lp-banner-resume-icon { font-size: 20px; flex-shrink: 0 }
        .lp-banner-resume-txt { flex: 1 }
        .lp-banner-resume-txt strong { color: #1e3a8a }
        .lp-banner-resume-btn {
          padding: 6px 12px; background: #1e3a8a; color: #fff;
          border: 0; border-radius: 8px; font-size: 12px; font-weight: 800;
          cursor: pointer; font-family: inherit; white-space: nowrap; flex-shrink: 0;
        }
        .lp-banner-resume-btn:hover { background: #1e40af }
        .lp-banner-resume-close {
          background: transparent; border: 0; color: #1e3a8a;
          font-size: 22px; line-height: 1; cursor: pointer; padding: 0 4px;
          font-family: inherit; flex-shrink: 0;
        }
        @media (min-width: 900px) {
          .lp-banner-resume { padding: 0 44px }
        }
      `}</style>
    </div>
  )
}

function Field({ label, children, required, hint }) {
  return (
    <label className="lp-field">
      <b>
        {label} {required && <span className="lp-req">*</span>}
      </b>
      {hint && <div style={{ fontSize: 12, color: '#64748b', marginTop: -2, marginBottom: 8, lineHeight: 1.4 }}>{hint}</div>}
      {children}
    </label>
  )
}

function Row({ label, children }) {
  return (
    <div className="lp-row">
      <span className="lp-row-tag">{label}</span>
      <span className="lp-row-val">{children}</span>
    </div>
  )
}

function BtnNav({ onBack, onNext, disabled, nextLabel = 'Siguiente →' }) {
  return (
    <div className="lp-nav-btns">
      <button className="lp-btn-back" onClick={onBack} style={{ visibility: onBack ? 'visible' : 'hidden' }}>← Atrás</button>
      <button className="lp-btn-next" onClick={onNext} disabled={disabled}>{nextLabel}</button>
    </div>
  )
}

const styles = `
:root {
  --navy:  #1a3461;
  --blue:  #1a5cb8;
  --teal:  #00A8B5;
  --cream: #F8FAFC;
  --ink:   #0f172a;
  --muted2:#64748b;
  --line2: #e2e8f0;
}

html, body { background: var(--cream); margin: 0 }
.lp-app {
  min-height: 100vh;
  background: var(--cream);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--navy);
  -webkit-font-smoothing: antialiased;
  display: flex; flex-direction: column;
}

/* Navbar */
.lp-nav {
  position: sticky; top: 0; z-index: 50;
  background: rgba(26,52,97,.94);
  backdrop-filter: blur(12px);
}
.lp-nav-inner {
  max-width: 1100px; margin: 0 auto;
  padding: 0 16px; height: 64px;
  display: flex; align-items: center; justify-content: space-between;
}
.lp-logo {
  font-size: 22px; font-weight: 900;
  color: #fff; text-decoration: none;
  letter-spacing: -.02em;
}
.lp-logo span { color: var(--teal) }
.lp-nav-actions { display: flex; gap: 10px; align-items: center }
.lp-nav-link {
  color: rgba(255,255,255,.75); text-decoration: none;
  font-size: 13px; font-weight: 500;
}
.lp-nav-link:hover { color: var(--teal) }
.lp-nav-logout {
  background: rgba(255,255,255,.12); color: #fff;
  border: 0; padding: 8px 14px; border-radius: 999px;
  font-size: 13px; font-weight: 600; cursor: pointer;
}
.lp-nav-logout:hover { background: rgba(255,255,255,.2) }

/* HERO */
.lp-hero {
  background: linear-gradient(135deg, #1a3461 0%, #1a5cb8 60%, #00A8B5 100%);
  padding: 56px 16px 70px;
  color: #fff;
}
.lp-hero-inner { max-width: 760px; margin: 0 auto; text-align: center }
.lp-hero-pill {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.18);
  padding: 7px 16px; border-radius: 999px;
  font-size: 12px; font-weight: 600; color: rgba(255,255,255,.92);
  margin-bottom: 18px;
}
.lp-hero-pill-dot {
  width: 7px; height: 7px; background: var(--teal); border-radius: 50%;
  animation: pulse 1.6s ease-in-out infinite;
}
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }

.lp-hero-h1 {
  font-size: clamp(28px, 5vw, 44px); font-weight: 900;
  line-height: 1.1; letter-spacing: -.02em; margin: 0 0 14px;
}
.lp-hero-h1 span { color: var(--teal) }
.lp-hero-sub {
  font-size: clamp(14px, 2.5vw, 17px);
  color: rgba(255,255,255,.82); line-height: 1.5;
  max-width: 560px; margin: 0 auto;
}

/* Stepper */
.lp-stepper-wrap {
  background: #fff; border-bottom: 1px solid var(--line2);
  padding: 16px;
}
.lp-stepper {
  max-width: 760px; margin: 0 auto;
  display: grid; grid-template-columns: repeat(5, 1fr); gap: 4px;
}
.lp-step { text-align: center; opacity: .4; transition: opacity .2s }
.lp-step.active, .lp-step.done { opacity: 1 }
.lp-step-num {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 50%;
  background: #e2e8f0; color: #64748b;
  font-weight: 800; font-size: 13px; margin-bottom: 4px;
}
.lp-step.active .lp-step-num { background: var(--teal); color: var(--navy) }
.lp-step.done .lp-step-num { background: #22c55e; color: #fff }
.lp-step-label {
  font-size: 10px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: .05em;
}
.lp-step.active .lp-step-label { color: var(--navy) }

/* Main */
.lp-main { max-width: 760px; width: 100%; margin: 0 auto; padding: 28px 16px 60px; flex: 1; box-sizing: border-box }

/* Card base */
.lp-card {
  background: #fff; border-radius: 20px;
  box-shadow: 0 8px 40px rgba(26,52,97,.08);
  border: 1px solid rgba(26,52,97,.05);
}
.lp-card-pad { padding: 24px }
@media (min-width: 640px) { .lp-card-pad { padding: 32px } }

/* PASO 0: Segmentos */
.lp-grid-segs {
  display: grid; grid-template-columns: 1fr; gap: 14px;
}
@media (min-width: 640px) { .lp-grid-segs { grid-template-columns: 1fr 1fr } }
.lp-seg-card {
  background: #fff; border-radius: 20px;
  padding: 24px; text-align: left;
  border: 2px solid transparent;
  box-shadow: 0 4px 20px rgba(26,52,97,.06);
  cursor: pointer; transition: transform .18s, box-shadow .18s, border-color .18s;
  font-family: inherit;
  display: flex; flex-direction: column; gap: 6px;
}
.lp-seg-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(26,52,97,.12);
}
.lp-seg-card.is-elegido {
  border-color: var(--teal);
  background: linear-gradient(180deg, #fff 0%, #f0fcfd 100%);
  box-shadow: 0 8px 28px rgba(0,196,212,.22);
}
.lp-seg-icon { font-size: 36px; line-height: 1; margin-bottom: 4px }
.lp-seg-titulo { font-size: 18px; font-weight: 800; color: var(--navy) }
.lp-seg-sub { font-size: 13px; color: var(--teal); font-weight: 700 }
.lp-seg-det { font-size: 13px; color: var(--muted2); line-height: 1.5; margin-top: 4px }
.lp-seg-check {
  margin-top: 10px; align-self: flex-start;
  background: var(--teal); color: var(--navy);
  padding: 4px 12px; border-radius: 999px;
  font-size: 11px; font-weight: 800; letter-spacing: .03em;
}

/* PASO 1: Toggle Essential / Premium */
.lp-tier-info {
  background: #f0fcfd; border: 1px solid #b3eef3;
  border-radius: 12px; padding: 14px 16px;
  font-size: 13px; line-height: 1.55; color: var(--navy);
  margin-bottom: 18px;
}
.lp-toggle-wrap { display: flex; justify-content: center; margin-bottom: 22px }
.lp-toggle {
  background: #fff; border: 1px solid var(--line2);
  border-radius: 999px; padding: 5px;
  display: inline-flex;
  box-shadow: 0 2px 8px rgba(26,52,97,.04);
}
.lp-toggle button {
  padding: 9px 22px; border-radius: 999px;
  border: 0; background: transparent;
  font-size: 12px; font-weight: 800; letter-spacing: .06em;
  color: rgba(26,52,97,.5); cursor: pointer;
  transition: background .15s, color .15s;
  font-family: inherit;
}
.lp-toggle button.on { background: var(--navy); color: #fff }

/* Kits */
.lp-kits { display: flex; flex-direction: column; gap: 16px }
.lp-kit {
  position: relative;
  background: #fff; border: 2px solid var(--line2);
  border-radius: 18px; padding: 22px;
  transition: transform .18s, box-shadow .18s, border-color .18s;
}
.lp-kit:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(26,52,97,.08) }
.lp-kit.is-elegido {
  border-color: var(--teal);
  box-shadow: 0 0 0 4px rgba(0,196,212,.15);
}
.lp-kit.is-premium { background: linear-gradient(180deg, #fff 0%, #fafdff 100%) }
.lp-kit.is-sin-stock {
  background: linear-gradient(180deg, #fafafa 0%, #f4f4f5 100%);
  border-color: #e4e4e7;
}
.lp-kit.is-sin-stock .lp-kit-precio,
.lp-kit.is-sin-stock .lp-kit-titulo h3,
.lp-kit.is-sin-stock .lp-kit-meta,
.lp-kit.is-sin-stock .lp-kit-desc { opacity: .55 }
.lp-kit.is-sin-stock:hover { transform: none; box-shadow: none }
.lp-kit-badge {
  position: absolute; top: -10px; left: 18px;
  background: var(--teal); color: var(--navy);
  padding: 4px 12px; border-radius: 999px;
  font-size: 11px; font-weight: 800; letter-spacing: .03em;
}
.lp-kit-badge.is-warning {
  background: #fef3c7; color: #78350f;
  border: 1px solid #fbbf24;
}
.lp-kit-head { margin-bottom: 14px }
.lp-kit-titulo { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 4px }
.lp-kit-titulo h3 { font-size: 20px; font-weight: 900; color: var(--navy); margin: 0; letter-spacing: -.01em }
.lp-kit-tier {
  font-size: 10px; font-weight: 800; letter-spacing: .06em;
  padding: 3px 10px; border-radius: 999px;
}
.tier-essential { background: #e8f5e9; color: #2e7d32 }
.tier-premium   { background: #e0f7fa; color: #006064 }
.tier-comercial { background: #fff3e0; color: #e65100 }
.lp-kit-meta { font-size: 12px; color: var(--muted2); font-weight: 600 }
.lp-kit-desc { font-size: 14px; color: #475569; line-height: 1.5; margin: 8px 0 0 }

.lp-kit-precio-row {
  display: flex; justify-content: space-between; align-items: center;
  gap: 14px; padding: 14px 0;
  border-top: 1px dashed var(--line2);
  border-bottom: 1px dashed var(--line2);
  margin-bottom: 14px;
}
.lp-kit-precio { font-size: 28px; font-weight: 900; color: var(--navy); letter-spacing: -.02em; line-height: 1 }
.lp-kit-precio-sub { font-size: 11px; color: var(--muted2); font-weight: 600; margin-top: 3px }
.lp-kit-ver {
  background: transparent; border: 0;
  font-size: 13px; font-weight: 700; color: var(--teal);
  cursor: pointer; padding: 8px 12px; border-radius: 8px;
  font-family: inherit; flex-shrink: 0;
}
.lp-kit-ver:hover { background: #f0fcfd }

.lp-kit-productos {
  list-style: none; padding: 14px 16px; margin: 0 0 14px;
  background: #f8fafc; border-radius: 12px;
}
.lp-kit-productos li {
  font-size: 13px; padding: 6px 0;
  border-bottom: 1px solid var(--line2);
  display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px;
}
.lp-kit-productos li:last-child { border-bottom: 0 }
.lp-kit-prod-cant { font-weight: 800; color: var(--teal); min-width: 28px }
.lp-kit-prod-nom { font-weight: 600; color: var(--navy) }
.lp-kit-prod-pres, .lp-kit-prod-marca { font-size: 12px; color: var(--muted2) }

.lp-kit-btn, .lp-kit-btn-on {
  width: 100%; padding: 14px 18px;
  border-radius: 999px; font-size: 14px; font-weight: 800;
  cursor: pointer; transition: transform .12s, background .15s;
  font-family: inherit; letter-spacing: .02em;
}
.lp-kit-btn { background: var(--navy); color: #fff; border: 0 }
.lp-kit-btn:hover { background: var(--blue) }
.lp-kit-btn-on { background: var(--teal); color: var(--navy); border: 0 }
.lp-kit-btn-on:hover { background: #fff; box-shadow: 0 0 0 2px var(--teal) }
.lp-kit-btn-wa {
  display: block; width: 100%; box-sizing: border-box;
  padding: 14px 18px; border-radius: 999px;
  font-size: 14px; font-weight: 800; letter-spacing: .02em;
  text-align: center; text-decoration: none; font-family: inherit;
  background: #25D366; color: #fff;
  transition: background .15s;
}
.lp-kit-btn-wa:hover { background: #1ebe57 }

.lp-link-todos {
  display: block; margin: 22px auto 0; padding: 10px 16px;
  background: transparent; border: 0;
  font-size: 13px; font-weight: 700; color: var(--blue);
  cursor: pointer; font-family: inherit;
}
.lp-link-todos:hover { text-decoration: underline }

/* Form */
.lp-field { display: block; margin-bottom: 16px }
.lp-field b {
  display: block; font-size: 11px; font-weight: 800;
  color: var(--navy); letter-spacing: .08em; text-transform: uppercase;
  margin-bottom: 6px;
}
.lp-req { color: #dc2626; margin-left: 2px }
.lp-card input, .lp-card select, .lp-card textarea {
  width: 100%; box-sizing: border-box;
  padding: 12px 14px; font-size: 15px;
  border: 1.5px solid var(--line2); border-radius: 10px;
  background: #fff; color: var(--navy); font-family: inherit;
  transition: border-color .15s, box-shadow .15s;
}
.lp-card input:focus, .lp-card select:focus, .lp-card textarea:focus {
  outline: none; border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(0,196,212,.15);
}
.lp-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
.lp-row-2-3 { display: grid; grid-template-columns: 2fr 1fr; gap: 12px }
@media (max-width: 520px) {
  .lp-row-2, .lp-row-2-3 { grid-template-columns: 1fr }
}

.lp-info {
  background: #f0fcfd; border: 1px solid #b3eef3;
  border-radius: 10px; padding: 12px 14px;
  font-size: 13px; color: var(--navy); margin-bottom: 14px;
  line-height: 1.5;
}
.lp-info-pago {
  background: linear-gradient(135deg, #f0fcfd 0%, #e0f7fa 100%);
  border: 1px solid #b3eef3; border-radius: 12px;
  padding: 14px 16px; font-size: 13px; line-height: 1.5;
  color: var(--navy); margin-top: 18px;
}
.lp-err {
  background: #fef2f2; border: 1px solid #fca5a5;
  border-radius: 10px; padding: 12px 14px;
  font-size: 13px; color: #991b1b; margin-top: 14px;
}

/* Aviso de privacidad (footer del wizard) */
.lp-privacy {
  margin: 24px auto 0; padding: 12px 16px;
  font-size: 11px; line-height: 1.5;
  color: var(--muted2); text-align: center;
  background: rgba(248,250,252,.6);
  border-radius: 10px; max-width: 540px;
}

/* Warning barrio detectado vs elegido */
.lp-warn-barrio {
  display: flex; gap: 12px; align-items: flex-start;
  margin-top: 14px; padding: 14px 16px;
  border-radius: 12px; font-size: 13px;
}
.lp-warn-barrio.is-warn { background: #fef3c7; border: 1.5px solid #fcd34d; color: #78350f }
.lp-warn-barrio.is-bloqueo { background: #fef2f2; border: 1.5px solid #fca5a5; color: #7f1d1d }
.lp-warn-barrio-icon { font-size: 24px; line-height: 1; flex-shrink: 0 }
.lp-warn-barrio-body { flex: 1 }
.lp-warn-barrio-body strong { display: block; font-size: 14px; margin-bottom: 6px }
.lp-warn-barrio-body p { margin: 0 0 10px; line-height: 1.45 }
.lp-warn-cta {
  display: inline-block; padding: 10px 18px; border-radius: 999px;
  background: var(--navy); color: #fff !important; text-decoration: none;
  border: 0; font-weight: 800; font-size: 13px; cursor: pointer;
  font-family: inherit; margin-right: 8px;
}
.lp-warn-cta:hover { background: var(--blue) }
.lp-warn-link {
  display: block; margin-top: 10px; background: transparent; border: 0;
  color: inherit; opacity: .65;
  font-size: 11px; font-weight: 600; text-decoration: underline;
  cursor: pointer; font-family: inherit; padding: 4px 0;
}

.lp-confirm-pin {
  display: flex; align-items: center; gap: 10px; margin-top: 12px;
  padding: 12px 14px; border-radius: 10px;
  font-size: 13px; font-weight: 600; cursor: pointer;
}
.lp-confirm-pin.ok   { border: 1.5px solid #22c55e; background: #f0fdf4; color: #166534 }
.lp-confirm-pin.pend { border: 1.5px solid #f59e0b; background: #fefce8; color: #854d0e }
.lp-confirm-pin input { width: 16px !important; height: 16px; margin: 0 }

.lp-btn-secondary {
  padding: 11px 18px; font-size: 13px; font-weight: 700;
  background: #fff; color: var(--navy);
  border: 1.5px solid var(--navy); border-radius: 999px;
  cursor: pointer; font-family: inherit;
  transition: background .15s, color .15s;
}
.lp-btn-secondary:hover:not(:disabled) { background: var(--navy); color: #fff }
.lp-btn-secondary:disabled { opacity: .5; cursor: not-allowed }

/* Resumen */
.lp-resumen { display: flex; flex-direction: column }
.lp-row {
  display: flex; justify-content: space-between; gap: 16px;
  align-items: baseline;
  padding: 12px 0; border-bottom: 1px dashed var(--line2);
  font-size: 14px;
}
.lp-row:last-child { border-bottom: 0 }
.lp-row-tag {
  color: var(--muted2); font-weight: 700; font-size: 11px;
  text-transform: uppercase; letter-spacing: .06em; flex-shrink: 0;
}
.lp-row-val { text-align: right; color: var(--navy); font-weight: 500 }

/* Nav buttons */
.lp-nav-btns {
  display: flex; justify-content: space-between; gap: 10px;
  margin-top: 24px; padding-top: 18px;
  border-top: 1px solid var(--line2);
}
.lp-btn-back {
  padding: 12px 18px; background: transparent; border: 0;
  color: var(--muted2); font-weight: 700; font-size: 14px;
  cursor: pointer; border-radius: 999px; font-family: inherit;
}
.lp-btn-back:hover { background: #f1f5f9; color: var(--navy) }
.lp-btn-next {
  padding: 14px 26px; font-size: 15px; font-weight: 800;
  background: linear-gradient(135deg, var(--blue), var(--teal));
  color: #fff; border: 0; border-radius: 999px;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 6px 20px rgba(0,196,212,.3);
  transition: transform .12s, box-shadow .12s, opacity .12s;
}
.lp-btn-next:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(0,196,212,.4);
}
.lp-btn-next:disabled { opacity: .45; cursor: not-allowed; box-shadow: none }

/* WhatsApp flotante */
.lp-wa {
  position: fixed; bottom: 24px; right: 24px; z-index: 60;
  background: #25d366; border-radius: 50%;
  width: 56px; height: 56px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 20px rgba(37,211,102,.45);
  transition: transform .15s;
  text-decoration: none;
}
.lp-wa:hover { transform: scale(1.08) }

/* FOOTER (idéntico a la web pública) */
.lp-footer { background: var(--navy); padding: 48px 16px 32px; margin-top: 0 }
.lp-footer-inner { max-width: 1100px; margin: 0 auto }
.lp-footer-grid {
  display: grid; grid-template-columns: 1fr;
  gap: 32px; margin-bottom: 32px;
}
@media (min-width: 640px) {
  .lp-footer-grid { grid-template-columns: 1.4fr 1fr 1fr }
}
.lp-footer-logo {
  font-size: 22px; font-weight: 900; color: #fff;
  margin-bottom: 10px; letter-spacing: -.02em;
}
.lp-footer-logo span { color: var(--teal) }
.lp-footer-desc {
  color: rgba(255,255,255,.5); font-size: 13px;
  line-height: 1.6; margin: 0;
}
.lp-footer-tit {
  color: #fff; font-weight: 700;
  font-size: 12px; text-transform: uppercase; letter-spacing: .08em;
  margin-bottom: 14px;
}
.lp-footer-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px }
.lp-footer-list a {
  color: rgba(255,255,255,.6); font-size: 13px;
  text-decoration: none; transition: color .15s;
}
.lp-footer-list a:hover { color: var(--teal) }
.lp-footer-bot {
  border-top: 1px solid rgba(255,255,255,.1);
  padding-top: 26px;
  display: flex; flex-direction: column; gap: 6px;
  align-items: center; text-align: center;
}
@media (min-width: 640px) {
  .lp-footer-bot { flex-direction: row; justify-content: space-between; text-align: left }
}
.lp-footer-bot p { margin: 0 }
.lp-footer-bot p:first-child { color: rgba(255,255,255,.4); font-size: 13px }
.lp-footer-bot p:last-child { color: rgba(255,255,255,.3); font-size: 11px }
`

/**
 * MiniFormCompletarDoc — se muestra en el paso 5 del wizard cuando el profile del cliente
 * no tiene `documento` (CI) o `nombre` cargado. Sin CI, el brick MP tokeniza pero MP antifraude
 * suele rechazar el cobro recurrente (score bajo). Actualiza profiles (documento + tipo='ci')
 * y avisa al padre vía onListo({ nombre, documento }) para poder renderizar el Brick.
 */
function MiniFormCompletarDoc({ nombreInicial, userId, onListo, onCancelar }) {
  const [nombre, setNombre] = useState(nombreInicial || '')
  const [doc, setDoc] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [err, setErr] = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    const nombreLimpio = nombre.trim()
    const docLimpio = doc.replace(/\D/g, '')
    if (!nombreLimpio) { setErr('Ingresá tu nombre y apellido'); return }
    if (docLimpio.length < 6) { setErr('Ingresá tu Cédula sin puntos ni guiones'); return }
    setGuardando(true)
    const { error } = await supabase.from('profiles')
      .update({ nombre: nombreLimpio, documento: docLimpio, documento_tipo: 'ci' })
      .eq('id', userId)
    if (error) { setErr('No pudimos guardar: ' + error.message); setGuardando(false); return }
    onListo({ nombre: nombreLimpio, documento: docLimpio })
  }

  return (
    <div style={{ padding: 18, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 17, color: '#1A3461' }}>
        Necesitamos un dato más
      </h3>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748B' }}>
        MercadoPago pide tu Cédula para autorizar el cobro recurrente. Se guarda una sola vez.
      </p>
      <form onSubmit={handleSubmit}>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <b style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#1A3461', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
            Nombre y apellido
          </b>
          <input
            type="text" required autoComplete="name"
            value={nombre} onChange={e => setNombre(e.target.value)}
            placeholder="Ej: María González"
            style={{ width: '100%', boxSizing: 'border-box', padding: '11px 13px', fontSize: 14, border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', color: '#1A3461', fontFamily: 'inherit' }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <b style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#1A3461', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
            Cédula (CI)
          </b>
          <input
            type="text" inputMode="numeric" required autoComplete="off"
            value={doc} onChange={e => setDoc(e.target.value)}
            placeholder="1.234.567-8"
            style={{ width: '100%', boxSizing: 'border-box', padding: '11px 13px', fontSize: 14, border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', color: '#1A3461', fontFamily: 'inherit' }}
          />
        </label>
        {err && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#7f1d1d', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 12 }}>
            {err}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button
            type="button" onClick={onCancelar} disabled={guardando}
            style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '10px 18px', borderRadius: 8, color: '#475569', cursor: guardando ? 'wait' : 'pointer', fontSize: 14 }}
          >
            ← Volver
          </button>
          <button
            type="submit" disabled={guardando}
            style={{ flex: 1, background: 'linear-gradient(135deg, #1a5cb8, #00A8B5)', color: '#fff', border: 0, padding: '10px 18px', borderRadius: 8, cursor: guardando ? 'wait' : 'pointer', fontSize: 14, fontWeight: 700 }}
          >
            {guardando ? 'Guardando…' : 'Guardar y continuar →'}
          </button>
        </div>
      </form>
    </div>
  )
}
